// SeaMap.jsx  (ShipTracker module)
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import {
	MapContainer,
	TileLayer,
	WMSTileLayer,
	Marker,
	CircleMarker,
	Circle,
	Popup,
	Tooltip,
	Polyline,
	LayersControl,
	ScaleControl,
	useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
// images/markerimages/cpapointmarker.png
import vesselcpa from '../../../images/markerimages/cpapointmarker.png';
import aisDataIcon from '../../../images/markerimages/aisData.png';
import pids from '../../../images/Refinery1.png';
import chImg from '../../../images/ch.png';
// import wbImg from '../../images/wb.png';
import kmpImg from '../../../images/kmp.png';
// import tlpImg from '../../images/tlp.png';
// import tpImg from '../../images/tp.png';

import arabiclan from '../../../arabic.json';
import englan from '../../../english.json';
import frenchlan from '../../../french.json';
import germanlan from '../../../german.json';
import hindilan from '../../../hindi.json';
import malayalamlan from '../../../malayalam.json';
import { datetimeCovertertolocal } from '../../datetime-converter';
// import dayjs from 'dayjs';

const { Overlay } = LayersControl;

// ============================================================
// FIX DEFAULT MARKER ICON PATHS
// ============================================================
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
	iconRetinaUrl:
		'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
	iconUrl:
		'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
	shadowUrl:
		'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ============================================================
// ROU MARKER ICONS (KMP / WB / TP / TLP / CH)
//
// Ported 1:1 from RealtimeEvents.jsx's Leaflet (OpenStreetMap) view so
// the exact same marker images/sizing are used on the OpenSeaMap view.
// ============================================================
const CHicon = L.icon({
	iconUrl: chImg,
	iconSize: [30, 40],
	iconAnchor: [18, 36],
});

// const WBicon = L.icon({
// 	iconUrl: wbImg,
// 	iconSize: [30, 40],
// 	iconAnchor: [18, 36],
// });

const KMPicon = L.icon({
	iconUrl: kmpImg,
	iconSize: [30, 40],
	iconAnchor: [18, 36],
});

// const TLPicon = L.icon({
// 	iconUrl: tlpImg,
// 	iconSize: [30, 40],
// 	iconAnchor: [18, 36],
// });

// const TPicon = L.icon({
// 	iconUrl: tpImg,
// 	iconSize: [30, 40],
// 	iconAnchor: [18, 36],
// });

// ============================================================
// LANGUAGE DATA (same pattern as GMap.jsx)
// ============================================================

const dashboardtype = process.env.REACT_APP_IDENTIFIER;
const permission = localStorage.getItem(`${dashboardtype.toUpperCase()}_PERMISSION`);



const langid =
	dashboardtype &&
	localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`) &&
	localStorage.getItem(`${dashboardtype.toUpperCase()}_LANGUAGE_ID`)
		? parseInt(
			localStorage.getItem(
				`${dashboardtype.toUpperCase()}_LANGUAGE_ID`
			)
		)
		: 2;

const langdata =
	langid && langid == 1
		? arabiclan
		: langid == 2
			? englan
			: langid == 3
				? frenchlan
				: langid == 4
					? germanlan
					: langid == 5
						? hindilan
						: langid == 6
							? malayalamlan
							: englan;
const oneviewType = process.env.REACT_APP_ONEVIEW_TYPE ? process.env.REACT_APP_ONEVIEW_TYPE : 'pipeline';
const oneviewtypeLabeluper = oneviewType && oneviewType.toLowerCase() == 'pipeline' ? langdata && langdata.PIPELINE : oneviewType.toLowerCase() == 'airport' ? langdata && langdata.AIRPORT : oneviewType.toLowerCase() == 'border' ? langdata && langdata.BORDER : oneviewType.toLowerCase() == 'railway' ? langdata && langdata.RAILWAY : oneviewType.toLowerCase() == 'subsea' ? langdata && langdata.SUBSEA : 'PIPELINE';
// ============================================================
// COLORS
// ============================================================

const TRACK_COLORS = [
	'#2563eb',
	'#dc2626',
	'#16a34a',
	'#d97706',
	'#9333ea'
];

const ALGORITHM_COLORS = [
	'#FF0000',
	'#0000FF',
	'#00AA00',
	'#FF9900',
	'#9900FF',
	'#00AAAA',
	'#FF00AA',
	'#795548',
	'#607D8B',
	'#8BC34A'
];
const colorForAlgorithmId = (id) => {
	if (id === undefined || id === null || id === '') return null;
	const str = String(id);
	let hash = 0;
	for (let i = 0; i < str.length; i += 1) {
		hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
	}
	return ALGORITHM_COLORS[hash % ALGORITHM_COLORS.length];
};

const getVesselColorKey = (vesselName) => {
	if (
		vesselName === undefined ||
		vesselName === null ||
		String(vesselName).trim() === ''
	) {
		return 'Unknown Vessel';
	}

	return String(vesselName).trim();
};

const pipecolor = { color: '#000000' };
const odmetercolor = { color: '#036CD6' };
const bordercolor = { color: '#fc8383' };
const optionalcolor = { color: 'orange' };

// ============================================================
// AIS LIVE VESSEL TRACKING (ais.openwaters.io, with fallback)
//
// Per openwaters' /v1/vessels docs: anonymous requests (no token)
// must filter to AT MOST 100 SQUARE DEGREES (bbox) or 10 MMSIs.
// A bbox that exceeds the area cap is rejected outright (400), it
// does NOT just come back with empty features — so every bbox this
// module ever sends, including the fallback, must stay under that
// cap. AIS_MAX_ANONYMOUS_AREA_DEG2 + clampBboxToAreaCap() enforce
// that centrally so this can't be silently broken again.
//
// NOTE: this layer never re-centers or re-fits the map on its own.
// If the local viewport has no vessels, it silently tries a broader
// fallback box (and, last resort, an aisstream.io snapshot) purely
// to have something to draw — but whatever it finds is rendered at
// its real position without moving the camera or surfacing any UI
// about it. The user's current view is left exactly where they put
// it, whether or not any vessels end up visible in it.
// ============================================================

const OPENWATERS_VESSELS_URL = 'https://ais.openwaters.io/v1/vessels';
const AIS_RESUBSCRIBE_DEBOUNCE_MS = 1200;
const AIS_MAX_ANONYMOUS_AREA_DEG2 = 100;

// A compact, known-to-have-coverage box (the dense Bergen/Stavanger
// stretch of the Norwegian coast, per the live aiscast map) used only
// when the user's local viewport returns zero vessels from
// openwaters. Deliberately small: 4° lat x 7° lon = 28 sq°, well
// under the 100 sq° anonymous cap.
const AIS_FALLBACK_BBOX = [58, 4, 62, 11]; // south, west, north, east

const bboxAreaDeg2 = ([south, west, north, east]) =>
	Math.max(0, north - south) * Math.max(0, east - west);

// Shrinks an oversized bbox to fit the anonymous area cap, keeping
// its center fixed, so a caller-supplied fallback/fixed bbox can
// never trigger a 400 from the API even if someone configures it too
// large by mistake.
const clampBboxToAreaCap = (bbox, maxAreaDeg2 = AIS_MAX_ANONYMOUS_AREA_DEG2) => {
	if (!bbox || bbox.length !== 4) return bbox;

	const [south, west, north, east] = bbox;
	const area = bboxAreaDeg2(bbox);
	if (area <= maxAreaDeg2 || area === 0) return bbox;

	const scale = Math.sqrt(maxAreaDeg2 / area) * 0.98; // small safety margin
	const latSpan = (north - south) * scale;
	const lonSpan = (east - west) * scale;
	const centerLat = (north + south) / 2;
	const centerLon = (east + west) / 2;

	return [
		centerLat - latSpan / 2,
		centerLon - lonSpan / 2,
		centerLat + latSpan / 2,
		centerLon + lonSpan / 2,
	];
};

const aisVesselIcon = (rotationDeg,) => {
	const rotation = Number.isFinite(rotationDeg) ? rotationDeg : 0;
	return L.divIcon({
		className: '',
		html: `
			<div style="width:22px;height:22px;transform:rotate(${rotation}deg);transform-origin:50% 50%;">
				<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">
					<polygon points="11,1 4,20 11,15.5 18,20" fill="#fccb2c" stroke="#000000" stroke-width="1" stroke-linejoin="round"/>
				</svg>
			</div>
		`,
		iconSize: [22, 22],
		iconAnchor: [11, 11],
		popupAnchor: [0, -10],
	});
};

// Order matches openwaters' documented format: minLat,minLon,maxLat,maxLon
// i.e. south,west,north,east.
const boundsToBboxParam = (bounds) => {
	if (!bounds) return null;
	const raw = [
		bounds.getSouth(),
		bounds.getWest(),
		bounds.getNorth(),
		bounds.getEast(),
	];
	return clampBboxToAreaCap(raw).join(',');
};

// ============================================================
// Shared parser: openwaters GeoJSON feature -> vessel object
// ============================================================
const parseOpenwatersFeatures = (geojson, includeKinds) => {
	const features = Array.isArray(geojson && geojson.features)
		? geojson.features
		: [];

	return features
		.filter((feature) => {
			const kind = feature && feature.properties
				? feature.properties.kind
				: undefined;
			return (
				!includeKinds ||
				includeKinds.length === 0 ||
				includeKinds.includes(kind)
			);
		})
		.map((feature) => {
			const coords =
				(feature.geometry && feature.geometry.coordinates) || [];
			const lng = coords[0];
			const lat = coords[1];
			const properties = feature.properties || {};

			const mmsi =
				properties.mmsi ??
				feature.id ??
				null;

			const kind =
				properties.kind ??
				null;

			const name =
				typeof properties.name === 'string' &&
				properties.name.trim() ? properties.name.trim() : null;

			const cog =
				Number.isFinite(Number(properties.cog)) ? Number(properties.cog) : null;

			const sog =
				Number.isFinite(Number(properties.sog)) ? Number(properties.sog) : null;

			const heading =
				Number.isFinite(Number(properties.heading)) ? Number(properties.heading) : null;

			const navStatus =
				properties.nav_status ??
				null;

			const seen =
				properties.seen ??
				null;

			const source =
				properties.source ??
				null;

			return {
				mmsi,
				kind,
				name,
				cog,
				sog,
				heading,
				navStatus,
				seen,
				source,
				lat: Number(lat),
				lng: Number(lng),
			};
		})
		.filter(
			(vessel) =>
				Number.isFinite(vessel.lat) && Number.isFinite(vessel.lng)
		);
};

const fetchOpenwatersBbox = (vesselsUrl, bboxParam, includeKinds, signal) => {
	const url = `${vesselsUrl}?bbox=${encodeURIComponent(bboxParam)}`;
	return fetch(url, { signal })
		.then((res) => {
			if (!res.ok) throw new Error(`AIS request failed: ${res.status}`);
			return res.json();
		})
		.then((geojson) => parseOpenwatersFeatures(geojson, includeKinds));
};

// ============================================================
// Optional second-tier fallback: a short-lived aisstream.io
// WebSocket snapshot. Only runs if an API key is supplied — without
// one this resolves to an empty array immediately, so it's always
// safe to call.
// ============================================================
const fetchAisstreamSnapshot = (
	apiKey,
	bboxSouthWestNorthEast,
	{ timeoutMs = 6000, maxVessels = 200 } = {}
) => {
	return new Promise((resolve) => {
		if (!apiKey || typeof WebSocket === 'undefined') {
			resolve([]);
			return;
		}

		const [south, west, north, east] = bboxSouthWestNorthEast;
		let settled = false;
		let socket;
		const collected = new Map();

		const finish = () => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			try {
				socket && socket.close();
			} catch (err) {
				// noop
			}
			resolve(Array.from(collected.values()));
		};

		const timer = setTimeout(finish, timeoutMs);

		try {
			socket = new WebSocket('wss://stream.aisstream.io/v0/stream');
		} catch (err) {
			clearTimeout(timer);
			resolve([]);
			return;
		}

		socket.onopen = () => {
			socket.send(JSON.stringify({
				APIKey: apiKey,
				BoundingBoxes: [[[south, west], [north, east]]],
				FilterMessageTypes: ['PositionReport'],
			}));
		};

		socket.onmessage = (event) => {
			try {
				const payload = JSON.parse(event.data);
				const meta = payload.MetaData || {};
				const report = payload.Message && payload.Message.PositionReport;

				const lat = Number(meta.latitude);
				const lng = Number(meta.longitude);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

				const mmsi = meta.MMSI ?? null;
				if (mmsi === null) return;

				collected.set(mmsi, {
					mmsi,
					kind: 'vessel',
					name: typeof meta.ShipName === 'string' && meta.ShipName.trim()
						? meta.ShipName.trim() : null,
					cog: report && Number.isFinite(Number(report.Cog)) ? Number(report.Cog) : null,
					sog: report && Number.isFinite(Number(report.Sog)) ? Number(report.Sog) : null,
					heading: report && Number.isFinite(Number(report.TrueHeading)) ? Number(report.TrueHeading) : null,
					navStatus: report && report.NavigationalStatus !== undefined ? report.NavigationalStatus : null,
					seen: meta.time_utc || null,
					source: 'aisstream.io',
					lat,
					lng,
				});

				if (collected.size >= maxVessels) finish();
			} catch (err) {
				// Ignore malformed frames.
			}
		};

		socket.onerror = finish;
		socket.onclose = finish;
	});
};

// NOTE: this layer intentionally does not accept/emit any
// "coverage state" callback and never touches the map's view
// (no fitBounds/setView calls anywhere in this component). It only
// ever adds/removes markers. Whatever bbox produced the vessels
// currently shown, the camera stays exactly where the user left it.
const AisVesselsLayer = ({
	vesselsUrl,
	pollIntervalMs,
	fixedBbox,
	includeKinds,
	maxVessels,
	fallbackBbox,
	aisstreamApiKey,
}) => {
	const map = useMap();

	const [vessels, setVessels] = useState([]);

	const fetchAbortRef = useRef(null);
	const pollTimerRef = useRef(null);
	const debounceTimerRef = useRef(null);
	const mountedRef = useRef(true);

	const clampedFallbackBbox = useMemo(
		() => (fallbackBbox ? clampBboxToAreaCap(fallbackBbox) : undefined),
		[fallbackBbox]
	);

	const applyLimit = useCallback((list) => (
		maxVessels > 0 && list.length > maxVessels
			? list.slice(0, maxVessels)
			: list
	), [maxVessels]);

	const fetchVessels = useCallback(() => {
		if (fetchAbortRef.current) fetchAbortRef.current.abort();
		const controller = new AbortController();
		fetchAbortRef.current = controller;

		const bboxParam =
			fixedBbox && fixedBbox.length === 4
				? clampBboxToAreaCap(fixedBbox).join(',')
				: boundsToBboxParam(map.getBounds());

		if (!bboxParam) return;

		fetchOpenwatersBbox(vesselsUrl, bboxParam, includeKinds, controller.signal)
			.then((localVessels) => {
				if (!mountedRef.current || controller.signal.aborted) return;

				if (localVessels.length > 0 || !clampedFallbackBbox) {
					setVessels(applyLimit(localVessels));
					return;
				}

				// Local viewport is empty — try the known-coverage fallback box
				// just to have something to draw. This never moves the map;
				// any vessels found here are simply rendered at their real
				// coordinates, which may or may not be inside the current view.
				return fetchOpenwatersBbox(
					vesselsUrl,
					clampedFallbackBbox.join(','),
					includeKinds,
					controller.signal
				).then((fallbackVessels) => {
					if (!mountedRef.current || controller.signal.aborted) return;

					if (fallbackVessels.length > 0) {
						setVessels(applyLimit(fallbackVessels));
						return;
					}

					// Still nothing — last resort: a short aisstream.io
					// snapshot, if an API key was provided. Safe no-op
					// otherwise (resolves to []).
					return fetchAisstreamSnapshot(aisstreamApiKey, clampedFallbackBbox)
						.then((streamVessels) => {
							if (!mountedRef.current || controller.signal.aborted) return;
							setVessels(applyLimit(streamVessels));
						});
				});
			})
			.catch((err) => {
				if (err && err.name !== 'AbortError') {
					// Swallow — supplementary layer.
				}
			});
	}, [map, vesselsUrl, fixedBbox, includeKinds, clampedFallbackBbox, aisstreamApiKey, applyLimit]);

	useEffect(() => {
		mountedRef.current = true;
		fetchVessels();

		pollTimerRef.current = setInterval(fetchVessels, pollIntervalMs);

		return () => {
			mountedRef.current = false;
			clearInterval(pollTimerRef.current);
			if (fetchAbortRef.current) fetchAbortRef.current.abort();
		};
	}, [vesselsUrl, pollIntervalMs]);

	useEffect(() => {
		if (fixedBbox && fixedBbox.length === 4) return undefined;

		const handleMoveEnd = () => {
			if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
			debounceTimerRef.current = setTimeout(
				fetchVessels,
				AIS_RESUBSCRIBE_DEBOUNCE_MS
			);
		};

		map.on('moveend zoomend', handleMoveEnd);
		return () => {
			map.off('moveend zoomend', handleMoveEnd);
			if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
		};
	}, [map, fixedBbox, fetchVessels]);

	return (
		<>
			{vessels.map((vessel) => {
				const rotation = Number.isFinite(vessel.heading)
					? vessel.heading
					: vessel.cog;
				const newTime = datetimeCovertertolocal(vessel.seen);

				return (
					<Marker
						key={`ais-${vessel.mmsi}`}
						position={[vessel.lat, vessel.lng]}
						icon={aisVesselIcon(rotation, '#1565c0')}
					>
						<Tooltip direction="top" offset={[0, -10]}>
							<strong>{vessel.name || `MMSI ${vessel.mmsi}`}</strong>
							<br />

							{(langdata && langdata.Latitude) || 'Latitude'}:
							{' '}
							{vessel.lat ?? 'No Data'}
							<br />
							{(langdata && langdata.Longitude) || 'Longitude'}:
							{' '}
							{vessel.lng ?? 'No Data'}
							<br />
						</Tooltip>

						<Popup>
							<strong>
								{vessel.name || ((langdata && langdata.Vessel) || 'Vessel')}
							</strong>
							<br />
							MMSI: {vessel.mmsi}
							<br />
							{(langdata && langdata.Latitude) || 'Latitude'}:
							{' '}
							{vessel.lat ?? 'No Data'}
							<br />
							{(langdata && langdata.Longitude) || 'Longitude'}:
							{' '}
							{vessel.lng ?? 'No Data'}
							<br />
							{(langdata && langdata.Speed) || 'Speed'}:
							{' '}
							{vessel.sog ?? 'No Data'} kn
							<br />
							{(langdata && langdata.Course) || 'Course'}:
							{' '}
							{vessel.cog ?? 'No Data'}°
							{vessel.seen && (
								<>
									<br />
									{(langdata && langdata.dateTime ? langdata.dateTime : 'Date Time')} : {(newTime.localdate + ' ' + newTime.localtime)}
								</>
							)}
							{vessel.source && (
								<>
									<br />
									{(langdata && langdata.Source) || 'Source'}: {vessel.source}
								</>
							)}
						</Popup>
					</Marker>
				);
			})}
		</>
	);
};

AisVesselsLayer.propTypes = {
	vesselsUrl: PropTypes.string.isRequired,
	pollIntervalMs: PropTypes.number,
	fixedBbox: PropTypes.arrayOf(PropTypes.number),
	includeKinds: PropTypes.arrayOf(PropTypes.string),
	maxVessels: PropTypes.number,
	fallbackBbox: PropTypes.arrayOf(PropTypes.number),
	aisstreamApiKey: PropTypes.string,
};

AisVesselsLayer.defaultProps = {
	pollIntervalMs: 15000,
	fixedBbox: undefined,
	includeKinds: ['vessel'],
	maxVessels: 500,
	fallbackBbox: undefined,
	aisstreamApiKey: undefined,
};

const vesselCpaIcon = L.icon({
	iconUrl: vesselcpa,
	iconSize: [28, 45],
	iconAnchor: [14, 45],
	popupAnchor: [0, -40],
});
const aisDataVesselIcon = L.icon({
	iconUrl: aisDataIcon,
	iconSize: [28, 45],
	iconAnchor: [14, 45],
	popupAnchor: [0, -40],
});

export { vesselCpaIcon, aisDataVesselIcon };

const pidsIcon = L.icon({
	iconUrl: pids,
	iconSize: [32, 32],
	iconAnchor: [16, 32],
	popupAnchor: [0, -28],
});

const labelIcon = (label, color) =>
	L.divIcon({
		html: `
			<div style="
				display:flex;
				align-items:center;
				justify-content:center;
				width:18px;
				height:18px;
				border-radius:50%;
				background:${color};
				color:#ffffff;
				font-size:10px;
				font-weight:bold;
				border:2px solid #ffffff;
				box-shadow:0 0 2px rgba(0,0,0,0.5);
			">
				${label}
			</div>
		`,
		className: '',
		iconSize: [18, 18],
		iconAnchor: [9, 9],
	});

// ============================================================
// FIBRE DETECTION ICON (orange marker used to identify a
// trajectory's fibre-side detection point(s) on the map — driven by
// the fiberLatitude / fiberLongitude / fiberDistance arrays).
//
// Kept as a DOM divIcon for reference / standalone use; the bulk
// fibre-detection layer draws the same shape directly on canvas
// (see 'diamond' in CanvasPointsLayer).
// ============================================================
// const peakCoordinateIcon = L.divIcon({
// 	className: '',
// 	html: `
// 		<div style="
// 			width: 10px;
// 			height: 10px;
// 			background: #f75a00;
// 			border: 2px solid #f75a00;
// 			outline: 1px solid #000000;
// 			transform: rotate(110deg);
// 			box-shadow: 0 1px 4px rgba(0,0,0,0.7);
// 			position: relative;
// 			display: flex;
// 			align-items: center;
// 			justify-content: center;
// 		">
// 			<span style="
// 				color: #000000;
// 				font-weight: bold;
// 				font-size: 13px;
// 				line-height: 1;
// 				transform: rotate(-90deg);
// 			"></span>
// 		</div>
// 	`,
// 	iconSize: [18, 18],
// 	iconAnchor: [6, 6],
// 	popupAnchor: [0, -12],
// });

const harbourIcon = L.divIcon({
	className: '',
	html: `
		<svg width="28" height="28" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
			<circle cx="17" cy="17" r="14.5" fill="none" stroke="#9c1263" stroke-width="3"/>
			<g transform="translate(17,17)" stroke="#9c1263" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round">
				<circle cx="0" cy="-5.95" r="2.55"/>
				<line x1="0" y1="-3.4" x2="0" y2="8.5"/>
				<line x1="-3.4" y1="-2" x2="3.4" y2="-2"/>
				<path d="M -5.95 0 H -8.5 A 8.5 8.5 0 0 0 8.5 0 H 5.95"/>
			</g>
		</svg>
	`,
	iconSize: [34, 34],
	iconAnchor: [17, 17],
	popupAnchor: [0, -16],
});

const EXPAND_ICON = `
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
		<path d="M8 3H5a2 2 0 0 0-2 2v3"/>
		<path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
		<path d="M3 16v3a2 2 0 0 0 2 2h3"/>
		<path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
	</svg>
`;

const COMPRESS_ICON = `
	<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
		<path d="M8 3v3a2 2 0 0 1-2 2H3"/>
		<path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
		<path d="M3 16h3a2 2 0 0 1 2 2v3"/>
		<path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
	</svg>
`;

const FullscreenControl = ({ position, fullscreenTargetRef }) => {
	const map = useMap();

	useEffect(() => {
		const container =
			(fullscreenTargetRef && fullscreenTargetRef.current) ||
			map.getContainer();

		const titleEnter = (langdata && langdata.FullScreen) ? langdata.FullScreen : 'Full Screen';
		const titleExit = (langdata && langdata.ExitFullScreen) ? langdata.ExitFullScreen : 'Exit Full Screen';

		const FullscreenLeafletControl = L.Control.extend({
			options: { position: position || 'topleft' },

			onAdd() {
				const btn = L.DomUtil.create('a', 'leaflet-control-fullscreen-btn leaflet-bar-part');
				btn.href = '#';
				btn.title = titleEnter;
				btn.setAttribute('role', 'button');
				btn.style.cssText =
					'display:flex;align-items:center;justify-content:center;width:30px;height:30px;' +
					'background:#ffffff;text-decoration:none;' + 'cursor:pointer;';
				btn.innerHTML = EXPAND_ICON;

				L.DomEvent.disableClickPropagation(btn);
				L.DomEvent.on(btn, 'click', L.DomEvent.stop);
				L.DomEvent.on(btn, 'click', () => {
					if (!document.fullscreenElement) {
						container.requestFullscreen?.();
					} else {
						document.exitFullscreen?.();
					}
				});

				this._btn = btn;
				return btn;
			},

			onRemove() {
				if (this._btn) {
					L.DomEvent.off(this._btn);
				}
			},
		});

		const control = new FullscreenLeafletControl();
		map.addControl(control);

		const handleFullscreenChange = () => {
			const isFs = document.fullscreenElement === container;
			if (control._btn) {
				control._btn.title = isFs ? titleExit : titleEnter;
				control._btn.innerHTML = isFs ? COMPRESS_ICON : EXPAND_ICON;
			}
			setTimeout(() => map.invalidateSize(), 150);
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
			map.removeControl(control);
		};
	}, [map, position, fullscreenTargetRef]);

	return null;
};

FullscreenControl.propTypes = {
	position: PropTypes.oneOf(['topleft', 'topright', 'bottomleft', 'bottomright']),
	fullscreenTargetRef: PropTypes.object
};

FullscreenControl.defaultProps = {
	position: 'topleft',
	fullscreenTargetRef: null
};

const CoordinateGrid = () => {
	const map = useMap();
	const groupRef = useRef(null);

	useEffect(() => {
		const group = L.layerGroup().addTo(map);
		groupRef.current = group;

		const stepForZoom = (zoom) => {
			if (zoom >= 12) return 0.05;
			if (zoom >= 10) return 0.1;
			if (zoom >= 8) return 0.5;
			if (zoom >= 6) return 1;
			if (zoom >= 4) return 2;
			return 5;
		};

		const draw = () => {
			group.clearLayers();

			const bounds = map.getBounds();
			const zoom = map.getZoom();
			const step = stepForZoom(zoom);

			const south = bounds.getSouth();
			const north = bounds.getNorth();
			const west = bounds.getWest();
			const east = bounds.getEast();

			const startLat = Math.floor(south / step) * step;
			const startLng = Math.floor(west / step) * step;

			const lineOptions = {
				color: '#333333',
				weight: 0.6,
				opacity: 0.55,
				interactive: false,
				dashArray: '2,4',
			};

			const labelStyle =
				'background:transparent;color:#333;font-size:10px;font-weight:600;' +
				'text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff;white-space:nowrap;';

			for (let lat = startLat; lat <= north; lat += step) {
				group.addLayer(L.polyline([[lat, west], [lat, east]], lineOptions));
				group.addLayer(
					L.marker([lat, west], {
						interactive: false,
						icon: L.divIcon({
							className: '',
							html: `<div style="${labelStyle}">${lat.toFixed(2)}°</div>`,
							iconSize: [0, 0],
							iconAnchor: [-4, 6],
						}),
					})
				);
			}

			for (let lng = startLng; lng <= east; lng += step) {
				group.addLayer(L.polyline([[south, lng], [north, lng]], lineOptions));
				group.addLayer(
					L.marker([south, lng], {
						interactive: false,
						icon: L.divIcon({
							className: '',
							html: `<div style="${labelStyle}">${lng.toFixed(2)}°</div>`,
							iconSize: [0, 0],
							iconAnchor: [-4, -14],
						}),
					})
				);
			}
		};

		draw();
		map.on('moveend zoomend', draw);

		return () => {
			map.off('moveend zoomend', draw);
			map.removeLayer(group);
		};
	}, [map]);

	return null;
};

const HarbourLayer = () => {
	const map = useMap();
	const scriptRef = useRef(null);
	const requestIdRef = useRef(0);

	useEffect(() => {
		const group = L.layerGroup().addTo(map);

		const removeScript = () => {
			if (scriptRef.current && scriptRef.current.parentNode) {
				scriptRef.current.parentNode.removeChild(scriptRef.current);
			}
			scriptRef.current = null;
		};

		const renderHarbours = (harbours) => {
			group.clearLayers();

			harbours.forEach((harbour) => {
				if (!Number.isFinite(harbour.lat) || !Number.isFinite(harbour.lon)) {
					return;
				}

				const displayName =
					(harbour.name && harbour.name.replace(/_/g, ' ')) ||
					((langdata && langdata.Harbour) || 'Harbour');

				const marker = L.marker([harbour.lat, harbour.lon], { icon: harbourIcon });

				marker.bindPopup(
					`<strong>${displayName}</strong>` +
					(harbour.url
						? `<br/><a href="${harbour.url}" target="_blank" rel="noreferrer">${(langdata && langdata.MoreInfo) ? langdata.MoreInfo : 'More info'}</a>`
						: '')
				);

				group.addLayer(marker);
			});
		};

		const loadFromOverpass = (bounds, requestId) => {
			const bbox = [
				bounds.getSouth(),
				bounds.getWest(),
				bounds.getNorth(),
				bounds.getEast(),
			].join(',');

			const query =
				'[out:json][timeout:25];(' +
				`node["seamark:type"="harbour"](${bbox});` +
				`node["seamark:type"="harbour_basin"](${bbox});` +
				`node["leisure"="marina"](${bbox});` +
				`node["harbour"="yes"](${bbox});` +
				');out body;';

			fetch('https://overpass-api.de/api/interpreter', {
				method: 'POST',
				body: query,
			})
				.then((res) => res.json())
				.then((data) => {
					if (requestId !== requestIdRef.current) return;

					const harbours = (data.elements || []).map((element) => ({
						lat: element.lat,
						lon: element.lon,
						name:
							(element.tags &&
								(element.tags.name || element.tags['seamark:name'])) ||
							null,
						url: null,
					}));

					renderHarbours(harbours);
				})
				.catch(() => {
					// Fail quietly, supplementary layer.
				});
		};

		const load = () => {
			const requestId = ++requestIdRef.current;
			const bounds = map.getBounds();
			const zoom = Math.round(map.getZoom());

			const collected = [];

			window.putHarbourMarker = (id, lon, lat, name, url) => {
				if (requestId !== requestIdRef.current) return;
				collected.push({ id, lon: parseFloat(lon), lat: parseFloat(lat), name, url });
			};

			removeScript();

			const params = new URLSearchParams({
				b: bounds.getSouth(),
				t: bounds.getNorth(),
				l: bounds.getWest(),
				r: bounds.getEast(),
				ucid: '0',
				maxSize: '5',
				zoom: String(zoom),
			});

			const script = document.createElement('script');
			script.src = `https://harbour.openseamap.org/getHarbours.php?${params.toString()}`;
			script.async = true;

			let settled = false;

			const finishPrimary = () => {
				if (settled || requestId !== requestIdRef.current) return;
				settled = true;

				if (collected.length > 0) {
					renderHarbours(collected);
				} else if (zoom >= 9) {
					loadFromOverpass(bounds, requestId);
				} else {
					group.clearLayers();
				}
			};

			script.onload = finishPrimary;
			script.onerror = () => {
				if (requestId !== requestIdRef.current) return;
				settled = true;
				if (zoom >= 9) {
					loadFromOverpass(bounds, requestId);
				} else {
					group.clearLayers();
				}
			};

			scriptRef.current = script;
			document.body.appendChild(script);

			setTimeout(finishPrimary, 4000);
		};

		load();
		map.on('moveend zoomend', load);

		return () => {
			requestIdRef.current += 1;
			map.off('moveend zoomend', load);
			map.removeLayer(group);
			removeScript();
			delete window.putHarbourMarker;
		};
	}, [map]);

	return null;
};

const CompassRoseControl = () => {
	const map = useMap();

	useEffect(() => {
		const CompassRoseLeafletControl = L.Control.extend({
			options: { position: 'bottomleft' },

			onAdd() {
				const div = L.DomUtil.create('div', 'compass-rose-control');
				div.style.cssText = 'pointer-events:none;';
				div.innerHTML = `
					<svg viewBox="0 0 100 100" width="64" height="64">
						<circle cx="50" cy="50" r="46" fill="rgba(255,255,255,0.85)" stroke="#333" stroke-width="1.5" />
						<polygon points="50,8 56,50 50,42 44,50" fill="#d32f2f" />
						<polygon points="50,92 56,50 50,58 44,50" fill="#333333" />
						<text x="50" y="20" font-size="11" text-anchor="middle" fill="#333">N</text>
						<text x="50" y="87" font-size="10" text-anchor="middle" fill="#333">S</text>
						<text x="15" y="54" font-size="10" text-anchor="middle" fill="#333">W</text>
						<text x="85" y="54" font-size="10" text-anchor="middle" fill="#333">E</text>
					</svg>
				`;
				return div;
			},
		});

		const control = new CompassRoseLeafletControl();
		map.addControl(control);

		return () => {
			map.removeControl(control);
		};
	}, [map]);

	return null;
};

const MapReadyBridge = ({ onReady }) => {
	const map = useMap();
	useEffect(() => {
		onReady(map);
	}, [map, onReady]);
	return null;
};

MapReadyBridge.propTypes = { onReady: PropTypes.func.isRequired };

// ============================================================
// MAP AUTO-CENTER
//
// Decides where the map should be looking, per two rules (mirrors the
// intent of RealtimeDetection.jsx's `mapCenter` computation, but kept
// live/reactive here rather than a one-shot value handed to `center`):
//   - If there are detections (any point across any trajectory),
//     fit the view so every detection is visible together.
//   - If there are NO detections, center/fit on the PIDS marker(s)
//     instead of wherever the map's initial `center` prop happened
//     to default to.
//
// Works the same for /detectioncluster and /detectioncollections data
// (both arrive here as `detectionTrajectories`).
//
// Guards against re-centering on every re-render: ShipTracker
// rebuilds `detectionTrajectories`/`pidsMarkers` as fresh array
// references on every render even when nothing actually changed, so
// this keys off the underlying point data itself (count + first/last
// coordinates + coordinate sums for detections, each marker's own
// lat/lng for PIDS) rather than the array identity, and only calls
// fitBounds/setView when that content key actually changes.
// ============================================================

// A coordinate that can safely be used to position the map.
const isUsableCoord = (lat, lng) =>
	Number.isFinite(lat) &&
	Number.isFinite(lng) &&
	Math.abs(lat) <= 90 &&
	Math.abs(lng) <= 180 &&
	!(lat === 0 && lng === 0);

const MapAutoCenter = ({ detectionTrajectories, pidsMarkers }) => {
	const map = useMap();
	const lastKeyRef = useRef(null);

	useEffect(() => {
		// ---- Collect every usable detection point (cluster OR collection data).
		// Falls back to the real-time points when a record has no algorithm points. ----
		const allPoints = [];
		(detectionTrajectories || []).forEach((trajectory) => {
			const source =
				trajectory.points && trajectory.points.length > 0
					? trajectory.points
					: (trajectory.realPoints || []);

			source.forEach((point) => {
				if (point && isUsableCoord(point.lat, point.lng)) {
					allPoints.push([point.lat, point.lng]);
				}
			});
		});

		const pidsPoints = (pidsMarkers || [])
			.filter((marker) => isUsableCoord(marker.lat, marker.lng))
			.map((marker) => [marker.lat, marker.lng]);

		const hasDetections = allPoints.length > 0;

		// ---- Content key: count + first/last + coordinate sums, so genuinely new
		// data always triggers a re-center, while unrelated re-renders (new array
		// references with identical content) do not. ----
		let key;
		if (hasDetections) {
			let sumLat = 0;
			let sumLng = 0;
			allPoints.forEach((p) => {
				sumLat += p[0];
				sumLng += p[1];
			});
			key = `det:${allPoints.length}:${allPoints[0].join(',')}:${allPoints[allPoints.length - 1].join(',')}:${sumLat.toFixed(5)}:${sumLng.toFixed(5)}`;
		} else {
			key = `pids:${pidsPoints.map((p) => p.join(',')).join('|')}`;
		}

		if (key === lastKeyRef.current) {
			return;
		}
		lastKeyRef.current = key;

		// Detections first; otherwise fall back to the PIDS marker(s).
		const target = hasDetections ? allPoints : pidsPoints;
		if (target.length === 0) {
			return;
		}

		// Make sure Leaflet has measured the container (the page shows a
		// full-screen spinner while loading) before computing the fit.
		map.invalidateSize({ animate: false });

		if (target.length === 1) {
			map.setView(
				target[0],
				hasDetections ? Math.max(map.getZoom(), 14) : map.getZoom(),
				{ animate: false }
			);
			return;
		}

		map.fitBounds(L.latLngBounds(target), {
			padding: [50, 50],
			maxZoom: 15,
			animate: false,
		});
	}, [map, detectionTrajectories, pidsMarkers]);

	return null;
};

MapAutoCenter.propTypes = {
	detectionTrajectories: PropTypes.array,
	pidsMarkers: PropTypes.array,
};

MapAutoCenter.defaultProps = {
	detectionTrajectories: [],
	pidsMarkers: [],
};

// ============================================================
// PLAY-OVERLAY LISTENER
//
// Why this exists: the Play checkbox lives inside a `LayersControl`
// `<Overlay>` whose entry is only added once `detectionTrajectories`
// has data (i.e. AFTER the control has already mounted with zero
// trajectories). Detecting that checkbox purely via the dummy probe
// layer's OWN `add`/`remove` events is unreliable once the overlay
// itself was registered post-mount — the events can simply never
// fire, which silently breaks Play with no console error.
//
// The robust fix: `L.Control.Layers` ALWAYS fires `overlayadd` /
// `overlayremove` on the MAP itself whenever ANY overlay checkbox is
// toggled, regardless of when that overlay entry was registered. We
// listen for those map-level events and match on the overlay's own
// display name instead of depending on the probe layer's lifecycle.
// ============================================================
const PlayOverlayListener = ({ name, onChange }) => {
	const map = useMap();

	useEffect(() => {
		if (!name) return undefined;

		const handleOverlayAdd = (e) => {
			if (e && e.name === name) {
				onChange(true);
			}
		};

		const handleOverlayRemove = (e) => {
			if (e && e.name === name) {
				onChange(false);
			}
		};

		map.on('overlayadd', handleOverlayAdd);
		map.on('overlayremove', handleOverlayRemove);

		return () => {
			map.off('overlayadd', handleOverlayAdd);
			map.off('overlayremove', handleOverlayRemove);
		};
	}, [map, name, onChange]);

	return null;
};

PlayOverlayListener.propTypes = {
	name: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};

PlayOverlayListener.defaultProps = {
	name: '',
};

const MapPositionBridge = ({ targetRef }) => {
	const map = useMap();

	useEffect(() => {
		const update = (latlng) => {
			if (!targetRef.current || !latlng) return;
			const zoom = map.getZoom();
			const lat = latlng.lat ?? 0;
			const lng = latlng.lng ?? 0;
			targetRef.current.textContent = `Zoom:${Math.round(zoom)} ${formatLatLon(lat, lng)}`;
		};

		const handleMouseMove = (e) => e?.latlng && update(e.latlng);
		const handleMoveEnd = () => {
			const center = map.getCenter();
			if (center) update(center);
		};

		map.on('mousemove', handleMouseMove);
		map.on('moveend zoomend', handleMoveEnd);

		if (map.getCenter()) {
			update(map.getCenter());
		}

		return () => {
			map.off('mousemove', handleMouseMove);
			map.off('moveend zoomend', handleMoveEnd);
		};
	}, [map, targetRef]);

	return null;
};

MapPositionBridge.propTypes = { targetRef: PropTypes.object.isRequired };

const formatLatLon = (lat, lng) => {
	const ns = lat >= 0 ? 'N' : 'S';
	const we = lng >= 0 ? 'E' : 'W';

	const latAbsMin = Math.abs(lat) * 60;
	const lngAbsMin = Math.abs(lng) * 60;

	const latDeg = Math.floor(latAbsMin / 60);
	const lngDeg = Math.floor(lngAbsMin / 60);

	const latMin = (latAbsMin - latDeg * 60).toFixed(3).padStart(6, '0');
	const lngMin = (lngAbsMin - lngDeg * 60).toFixed(3).padStart(6, '0');

	return `${ns}${latDeg}\u00B0${latMin}' ${we}${lngDeg}\u00B0${lngMin}'`;
};

const buildPermalink = (position) => {
	if (typeof window === 'undefined' || !position) return '';
	try {
		const url = new URL(window.location.href);
		url.hash = `map=${Math.round(position.zoom)}/${position.lat.toFixed(5)}/${position.lng.toFixed(5)}`;
		return url.toString();
	} catch (err) {
		return '';
	}
};

const toolbarStyles = {
	wrapper: {
		position: 'absolute',
		top: 8,
		left: 54,
		zIndex: 1000,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		width: 'fit-content',
		background: '#e9edf1',
		border: '1px solid #b9c2cc',
		borderRadius: 4,
		boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
		fontFamily: 'Tahoma, Verdana, Arial, sans-serif',
		fontSize: 12,
		color: '#222',
		padding: '3px 6px',
	},
	logo: { fontSize: 15, padding: '0 4px' },
	searchWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
	searchInput: {
		width: 190,
		height: 22,
		border: '1px solid #9aa4af',
		borderRadius: 2,
		padding: '0 6px',
		fontSize: 12,
	},
	searchBtn: {
		height: 24,
		border: '1px solid #9aa4af',
		borderLeft: 'none',
		background: '#f4f6f8',
		cursor: 'pointer',
	},
	searchDropdown: {
		position: 'absolute',
		top: 26,
		left: 0,
		width: 300,
		maxHeight: 220,
		overflowY: 'auto',
		background: '#fff',
		border: '1px solid #9aa4af',
		boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
		zIndex: 1001,
	},
	searchResultRow: {
		padding: '6px 8px',
		fontSize: 12,
		borderBottom: '1px solid #eee',
		cursor: 'pointer',
	},
	menuItem: {
		padding: '0 8px',
		height: 26,
		display: 'flex',
		alignItems: 'center',
		gap: 4,
		cursor: 'pointer',
		userSelect: 'none',
		whiteSpace: 'nowrap',
		borderRadius: 3,
	},
	menuItemWrap: { position: 'relative' },
	dropdown: {
		position: 'absolute',
		top: 28,
		left: 0,
		minWidth: 200,
		background: '#fff',
		border: '1px solid #9aa4af',
		boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
		zIndex: 1001,
		padding: '4px 0',
	},
	dropdownRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '5px 10px',
		fontSize: 12,
		cursor: 'pointer',
	},
	dropdownDivider: { borderTop: '1px solid #e2e6ea', margin: '4px 0' },
};

const bottomBarStyles = {
	wrapper: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		height: 22,
		zIndex: 1000,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		background: 'rgba(233,237,241,0.9)',
		borderTop: '1px solid #b9c2cc',
		fontFamily: 'Tahoma, Verdana, Arial, sans-serif',
		fontSize: 11,
		color: '#333',
		padding: '0 8px',
	},
	attribution: { display: 'flex', gap: 4 },
	right: { display: 'flex', alignItems: 'center', gap: 12 },
	permalink: { color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' },
	position: { fontVariantNumeric: 'tabular-nums' },
};

const TopToolbar = ({ map, layerVisibility, onToggleLayer, showAisMenuItem }) => {
	const [viewOpen, setViewOpen] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const containerRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				setViewOpen(false);
				setSearchResults([]);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const runSearch = async () => {
		if (!searchText.trim()) return;
		try {
			const res = await fetch(
				`https://nominatim.openstreetmap.org/search?format=json&limit=6&q=${encodeURIComponent(searchText)}`
			);
			const data = await res.json();
			setSearchResults(data || []);
		} catch (err) {
			setSearchResults([]);
		}
	};

	const jumpToResult = (result) => {
		if (!map) return;
		map.setView([parseFloat(result.lat), parseFloat(result.lon)], 14);
		setSearchResults([]);
		setSearchText(result.display_name);
	};

	const viewMenuItems = [
		{ key: 'seaMarks', label: (langdata && langdata.SeaMarks) || 'Sea Marks' },
		{ key: 'harbours', label: (langdata && langdata.Harbours) || 'Harbours' },
		{ key: 'aerialPhoto', label: (langdata && langdata.AerialPhoto) || 'Aerial photo' },
		{ key: 'coordinateGrid', label: (langdata && langdata.CoordinateGrid) || 'Coordinate Grid' },
		{ key: 'compassRose', label: (langdata && langdata.Compass) || 'Compass' },
		{ key: 'waterDepth', label: (langdata && langdata.WaterDepth) || 'Water depth' },
		{ key: 'depthContours', label: (langdata && langdata.DepthContours) || 'Depth contours ' },
		...(showAisMenuItem
			? [{ key: 'aisVessels', label: (langdata && langdata.AISVessels) || 'AIS Vessels' }]
			: []),
	];

	return (
		<div ref={containerRef} style={toolbarStyles.wrapper}>
			<div style={toolbarStyles.logo}>⚓</div>

			<div style={toolbarStyles.searchWrap}>
				<input
					style={toolbarStyles.searchInput}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && runSearch()}
					placeholder={(langdata && langdata.Search) || 'Search...'}
				/>
				<button type="button" style={toolbarStyles.searchBtn} onClick={runSearch}>
					🔍
				</button>

				{searchResults.length > 0 && (
					<div style={toolbarStyles.searchDropdown}>
						{searchResults.map((result) => (
							<div
								key={result.place_id}
								style={toolbarStyles.searchResultRow}
								onClick={() => jumpToResult(result)}
							>
								{result.display_name}
							</div>
						))}
					</div>
				)}
			</div>

			<div style={toolbarStyles.menuItemWrap}>
				<div
					style={toolbarStyles.menuItem}
					onClick={() => setViewOpen((prev) => !prev)}
				>
					👁 {(langdata && langdata.View) || 'View'}
				</div>

				{viewOpen && (
					<div style={toolbarStyles.dropdown}>
						{viewMenuItems.map((item) => (
							<label key={item.key} style={toolbarStyles.dropdownRow}>
								<input
									type="checkbox"
									checked={!!layerVisibility[item.key]}
									onChange={() => onToggleLayer(item.key)}
								/>
								{item.label}
							</label>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

TopToolbar.propTypes = {
	map: PropTypes.object,
	layerVisibility: PropTypes.object.isRequired,
	onToggleLayer: PropTypes.func.isRequired,
	showAisMenuItem: PropTypes.bool,
};

TopToolbar.defaultProps = { map: null, showAisMenuItem: true };

const BottomInfoBar = ({ map, positionRef }) => {
	const [copied, setCopied] = useState(false);

	const copyPermalink = async () => {
		if (!map) return;
		const center = map.getCenter();
		const zoom = map.getZoom();
		const link = buildPermalink({ lat: center.lat, lng: center.lng, zoom });
		if (!link) return;
		try {
			await navigator.clipboard.writeText(link);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch (err) {
			// Clipboard access can fail in some contexts; fail silently.
		}
	};

	return (
		<div style={bottomBarStyles.wrapper}>
			<div style={bottomBarStyles.attribution}>
				<span>
					© <a href="https://openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors
				</span>
				<span>·</span>
				<span>
					© <a href="https://www.openseamap.org" target="_blank" rel="noreferrer">OpenSeaMap</a> contributors
				</span>
				<span>·</span>
				<span>
					AIS © <a href="https://openwaters.io" target="_blank" rel="noreferrer">Open Waters</a>
				</span>
			</div>

			<div style={bottomBarStyles.right}>
				<span style={bottomBarStyles.permalink} onClick={copyPermalink}>
					{copied ? ((langdata && langdata.Copied) || 'Copied!') : ((langdata && langdata.Permalink) || 'Permalink')}
				</span>

				<span ref={positionRef} style={bottomBarStyles.position} />
			</div>
		</div>
	);
};

BottomInfoBar.propTypes = {
	map: PropTypes.object,
	positionRef: PropTypes.object.isRequired,
};

BottomInfoBar.defaultProps = { map: null };

// ============================================================
// DETECTION-POINT HOVER CONTENT
//
// Shown on hover for every plotted detection point (the starting
// vessel-CPA marker and every point after it along a trajectory).
// Fixed field set, per spec: Algorithm, PIDS Name, Channel, Start
// Fibre Length, End Fibre Length, Start Time.
//
// `info.startTimeHover` is expected to already be a display-ready
// string (ShipTracker/RealtimeDetection.jsx runs it through
// datetimeCovertertolocal before handing it to SeaMap) — it is
// rendered as-is here and must NOT be reformatted again.
// ============================================================

const DetectionHoverContent = ({ info, pointIndex, totalPoints }) => {
	const idx = Number.isInteger(pointIndex) ? pointIndex : 0;
	// Picks the value for this point. If the array is the same length as the
	// number of points, use the index directly. If it is shorter/longer
	// (e.g. 6 fibre values vs 27 ship points), map the point proportionally.
	const pickAt = (data) => {
		if (!Array.isArray(data)) return data;
		if (data.length === 0) return undefined;

		if (
			!totalPoints ||
			totalPoints <= 1 ||
			data.length === totalPoints
		) {
			return data[idx];
		}

		if (data.length === 1) return data[0];

		const mapped = Math.round(
			(idx * (data.length - 1)) / (totalPoints - 1)
		);
		return data[Math.min(Math.max(mapped, 0), data.length - 1)];
	};

	const formatNum = (value) => {
		if (value === undefined || value === null || value === '') return 'No Data';
		const n = Number(value);
		return Number.isFinite(n) ? n : value;
	};

	const sourceDistance = formatNum(pickAt(info?.sourceDistances));
	const fiberDistance = formatNum(pickAt(info?.fiberDistance));
	const trackType = info?.trackType ? info?.trackType : null;
	const sourceSpeed = formatNum(pickAt(info?.sourceSpeedms));
	const startTime = pickAt(info?.startTimeHover);

	return (
		<>
			{['superadmin'].includes(permission) && (
				<>
					{langdata?.Algorithm || 'Algorithm'}
					{': '}
					{info?.detectionAlgorithmTypeName || 'No Data'}
					<br />
				</>
			)}

			{langdata?.PidsName || 'Pids Name'}
			{': '}
			{info?.pidsName || 'No Data'}
			<br />

			{langdata?.Channel || 'Channel'}
			{': '}
			{info?.channelName || 'No Data'}
			<br />

			{trackType !== null ? null :
				<>
					{langdata?.StartFibreLength
						? langdata.StartFibreLength + ' (m)'
						: 'Start Fibre Length (m)'}
					{': '}
					{info && (info.startFiberLength || info.startFiberLength === 0)
						? info.startFiberLength
						: 'No Data'}
					<br />

					{langdata?.EndFibreLength
						? langdata.EndFibreLength + ' (m)'
						: 'End Fibre Length (m)'}
					{': '}
					{info && (info.endFiberLength || info.endFiberLength === 0)
						? info.endFiberLength
						: 'No Data'}
					<br />
					{langdata?.VesselName || 'Vessel Name'}
					{': '}
					{info?.vesselName || 'No Data'}
					<br />
				</>
			}

			{langdata?.StartTime || 'Start Time'}
			{': '}
			{startTime || 'No Data'}
			<br />



			{langdata?.SourceDistance
				? langdata.SourceDistance + ' (m)'
				: 'Source Distance (m)'}
			{': '}
			{sourceDistance}
			<br />

			{langdata?.FibreDistance
				? langdata.FibreDistance + ' (m)'
				: 'Fibre Distance (m)'}
			{': '}
			{fiberDistance}
			<br />
			{trackType !== null ?
				<>
					{langdata?.trackType
						? langdata.trackType
						: 'Track Type'}
					{': '}
					{trackType !== null ? trackType : 'No Data'}
					<br />
				</> : null}
			{trackType !== null ? null :
				<>
					{langdata?.Speed
						? langdata.Speed + ' (m/s)'
						: 'Speed (m/s)'}
					{': '}
					{sourceSpeed}
				</>}
		</>
	);
};

DetectionHoverContent.propTypes = {
	info: PropTypes.shape({
		detectionAlgorithmTypeName: PropTypes.string,
		channelName: PropTypes.string,
		pidsName: PropTypes.string,
		vesselName: PropTypes.string,
		trackType: PropTypes.string,
		startFiberLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		endFiberLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		startTimeHover: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
		sourceDistances: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
		fiberDistance: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
		sourceSpeedms: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
	}),
	pointIndex: PropTypes.number,
	totalPoints: PropTypes.number,
};

DetectionHoverContent.defaultProps = {
	info: null,
	pointIndex: 0,
	totalPoints: 0,
};

const AsnHoverContent = ({ name, point }) => {
	const dt = point.datetime ? datetimeCovertertolocal(point.datetime) : null;
	const speedText =
		point.speed !== null && point.speed !== undefined && point.speed !== ''
			? `${point.speed}`
			: 'No Data';

	return (
		<>
			{(langdata && langdata.Name ? langdata.Name : 'Name')}
			:
			{' '}
			{name || 'No Data'}
			<br />

			{(langdata && langdata.Speed ? langdata.Speed + ' (m/s)' : 'Speed (m/s)')}
			:
			{' '}
			{speedText}
			<br />

			{(langdata && langdata.Latitude ? langdata.Latitude : 'Latitude')}
			:
			{' '}
			{point.lat}
			<br />

			{(langdata && langdata.Longitude ? langdata.Longitude : 'Longitude')}
			:
			{' '}
			{point.lng}
			<br />

			{(langdata && langdata.DateTime ? langdata.DateTime : 'Date Time')}
			:
			{' '}
			{dt ? `${dt.localdate} ${dt.localtime}` : 'No Data'}
		</>
	);
};

AsnHoverContent.propTypes = {
	name: PropTypes.string,
	point: PropTypes.shape({
		lat: PropTypes.number,
		lng: PropTypes.number,
		speed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		datetime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	}).isRequired,
};

AsnHoverContent.defaultProps = { name: '' };

// ============================================================
// PER-POINT VALUE HELPER
// ============================================================

// const pickValueForIndex = (data, index) => {
// 	if (Array.isArray(data)) {
// 		return index >= 0 && index < data.length
// 			? data[index]
// 			: null;
// 	}
// 	return data ?? null;
// };

// Builds the fibre points ({lat, lng, distance}) of one trajectory from its
// parallel fiberLatitude / fiberLongitude / fiberDistance arrays.
const buildFiberPoints = (trajectory) => {
	const lngSource = Array.isArray(trajectory.fiberLongitude) ? trajectory.fiberLongitude : [];
	const distSource = Array.isArray(trajectory.fiberDistance) ? trajectory.fiberDistance : [];

	return (Array.isArray(trajectory.fiberLatitude) ? trajectory.fiberLatitude : [])
		.map((lat, fiberIndex) => {
			const parsedLat = parseFloat(lat);
			const parsedLng = parseFloat(lngSource[fiberIndex]);

			if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
				return null;
			}

			const rawDistance = distSource[fiberIndex];
			const distance =
				rawDistance !== undefined && rawDistance !== null && !Number.isNaN(Number(rawDistance))
					? Number(rawDistance).toFixed(2)
					: null;

			return { lat: parsedLat, lng: parsedLng, distance };
		})
		.filter(Boolean);
};

// ============================================================
// PERFORMANCE: ARRAY-BASED SINGLE-CANVAS POINT LAYER
//
// (Same technique as the RealtimeDetection SeaMap.)
//
// Plotting tens of thousands of points as individual Leaflet layers
// (even canvas CircleMarkers) creates one object + one hit-test entry
// per point, which is what froze the UI. Instead, points are passed
// to the map as ARRAYS ("series"):
//
//   { kind: 'points',  lats: [], lngs: [], radius, style,
//     tooltipAt(i), tooltipOptions }
//   { kind: 'markers', lats: [], lngs: [], icon | shape:'diamond',
//     tooltipAt(i), popupAt(i), tooltipOptions, popupOptions }
//
// CanvasPointsLayer converts each series to typed arrays ONCE and draws
// everything on ONE <canvas>:
//
//   • points are projected with plain Web-Mercator math (no per-point
//     LatLng/Point objects);
//   • only points inside the (padded) viewport are drawn;
//   • every style group is drawn as ONE path (one fill + one stroke);
//   • the canvas is redrawn only on moveend / zoom end (panning and
//     zoom animation use a CSS transform, like Leaflet's own renderer);
//   • hover tooltips / click popups are resolved through a lazily built
//     pixel grid and rendered only for the single point under the
//     cursor (renderToStaticMarkup on demand).
//
// Marker icons (start / CPA / AIS images, fibre-detection diamond) are
// drawn on the same canvas, so there are no DOM marker nodes for
// detections at all.
// ============================================================
const BULK_PANE = 'bulkPointsPane';
const DEDUPE_CELL = 4;
const DEDUPE_ENABLED = false;
const HIT_CELL = 16;
const TWO_PI = Math.PI * 2;
const DIAMOND_ANGLE = (110 * Math.PI) / 180;
const MAX_MERC_LAT = 85.0511287798;

const spriteCache = new Map();
const loadSprite = (url, onLoad) => {
	let rec = spriteCache.get(url);
	if (!rec) {
		const img = new Image();
		rec = { img, loaded: false, callbacks: [] };
		img.onload = () => {
			rec.loaded = true;
			rec.callbacks.forEach((cb) => cb());
			rec.callbacks = [];
		};
		img.src = url;
		spriteCache.set(url, rec);
	}
	if (!rec.loaded && onLoad) rec.callbacks.push(onLoad);
	return rec;
};

const lazyHtml = (renderFn) => () => {
	try {
		return renderToStaticMarkup(renderFn());
	} catch (err) {
		return '';
	}
};

const CanvasPointsLayer = L.Layer.extend({
	options: { pane: BULK_PANE, padding: 0.5 },

	initialize(seriesList, options) {
		L.setOptions(this, options);
		this._series = [];
		this._groups = [];
		this._hitCells = 1;
		this._build(seriesList || []);
	},

	// ---------- data preparation (runs once per layer) ----------
	_build(list) {
		const groupMap = new Map();
		const rad = Math.PI / 180;

		list.forEach((s) => {
			const lats = s.lats || [];
			const lngs = s.lngs || [];
			const n = Math.min(lats.length, lngs.length);
			if (!n) return;

			const mx = new Float64Array(n);
			const my = new Float64Array(n);
			for (let i = 0; i < n; i += 1) {
				const lat = Number(lats[i]);
				const lng = Number(lngs[i]);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
					mx[i] = NaN;
					my[i] = NaN;
				} else {
					const clamped = Math.max(Math.min(lat, MAX_MERC_LAT), -MAX_MERC_LAT);
					const sin = Math.sin(clamped * rad);
					mx[i] = (lng + 180) / 360;
					my[i] = 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
				}
			}

			let type;
			let key;
			let proto = {};

			if (s.kind === 'points') {
				const st = s.style || {};
				const r = s.radius || 5;
				type = 'circle';
				key = `c|${r}|${st.color}|${st.weight}|${st.fillColor}|${st.fillOpacity}|${st.opacity}`;
				proto = { r, style: st, box: [-r - 2, -r - 2, r + 2, r + 2] };
			} else {
				const iconOpts = s.icon && s.icon.options;
				const url = iconOpts && iconOpts.iconUrl;
				if (s.shape !== 'diamond' && url) {
					const size = iconOpts.iconSize || [28, 45];
					const anchor = iconOpts.iconAnchor || [size[0] / 2, size[1]];
					type = 'image';
					key = `i|${url}`;
					proto = {
						url,
						size,
						anchor,
						popupAnchor: iconOpts.popupAnchor || [0, 0],
						box: [-anchor[0], -anchor[1], size[0] - anchor[0], size[1] - anchor[1]],
					};
				} else {
					type = 'diamond';
					key = 'd';
					proto = { popupAnchor: [0, -12], box: [-8, -8, 8, 8] };
				}
			}

			let group = groupMap.get(key);
			if (!group) {
				group = { type, key, series: [], ...proto };
				groupMap.set(key, group);
			}

			const series = {
				_idx: this._series.length,
				mx,
				my,
				n,
				lats,
				lngs,
				tooltipAt: s.tooltipAt || null,
				popupAt: s.popupAt || null,
				tooltipOptions: s.tooltipOptions || {},
				popupOptions: s.popupOptions || {},
				hittable: !!(s.tooltipAt || s.popupAt),
				group,
			};
			group.series.push(series);
			this._series.push(series);
		});

		const order = { circle: 0, diamond: 1, image: 2 };
		this._groups = Array.from(groupMap.values()).sort((a, b) => order[a.type] - order[b.type]);

		let extent = 0;
		this._groups.forEach((g) => {
			extent = Math.max(extent, ...g.box.map((v) => Math.abs(v)));
		});
		this._hitCells = Math.max(1, Math.ceil(extent / HIT_CELL));
	},

	// ---------- Leaflet lifecycle ----------
	onAdd(map) {
		this._map = map;

		if (!map.getPane(BULK_PANE)) {
			const pane = map.createPane(BULK_PANE);
			pane.style.zIndex = 450;
			pane.style.pointerEvents = 'none';
		}

		const canvas = L.DomUtil.create('canvas', 'leaflet-bulk-points');
		if (map._zoomAnimated) L.DomUtil.addClass(canvas, 'leaflet-zoom-animated');
		canvas.style.pointerEvents = 'none';
		canvas.style.maxWidth = 'none';
		map.getPane(this.options.pane).appendChild(canvas);

		this._canvas = canvas;
		this._ctx = canvas.getContext('2d');
		this._hoverKey = null;
		this._update();
	},

	onRemove() {
		if (this._raf) cancelAnimationFrame(this._raf);
		if (this._hoverRaf) cancelAnimationFrame(this._hoverRaf);
		this._raf = null;
		this._hoverRaf = null;
		this._closeTip();
		if (this._canvas && this._canvas.parentNode) {
			this._canvas.parentNode.removeChild(this._canvas);
		}
		this._canvas = null;
		this._ctx = null;
		this._drawn = null;
		this._grid = null;
		this._map = null;
	},

	getEvents() {
		const events = {
			viewreset: this._schedule,
			moveend: this._schedule,
			zoom: this._onZoom,
			zoomstart: this._closeTip,
			mousemove: this._onMouseMove,
			mouseout: this._closeTip,
			click: this._onClick,
		};
		if (this._map && this._map._zoomAnimated) {
			events.zoomanim = this._onAnimZoom;
		}
		return events;
	},

	// ---------- redraw scheduling ----------
	_schedule() {
		if (this._raf || !this._map) return;
		this._raf = requestAnimationFrame(() => {
			this._raf = null;
			this._update();
		});
	},

	_onAnimZoom(e) {
		this._updateTransform(e.center, e.zoom);
	},

	_onZoom() {
		if (!this._map) return;
		this._updateTransform(this._map.getCenter(), this._map.getZoom());
	},

	_updateTransform(center, zoom) {
		const map = this._map;
		if (!map || !this._canvas || !this._center) return;

		const scale = map.getZoomScale(zoom, this._zoom);
		const viewHalf = map.getSize().multiplyBy(0.5 + this.options.padding);
		const currentCenterPoint = map.project(this._center, zoom);
		const topLeftOffset = viewHalf
			.multiplyBy(-scale)
			.add(currentCenterPoint)
			.subtract(map._getNewPixelOrigin(center, zoom));

		if (L.Browser.any3d) {
			L.DomUtil.setTransform(this._canvas, topLeftOffset, scale);
		} else {
			L.DomUtil.setPosition(this._canvas, topLeftOffset);
		}
	},

	_update() {
		const map = this._map;
		const canvas = this._canvas;
		if (!map || !canvas || !map._loaded) return;

		const p = this.options.padding;
		const size = map.getSize();
		const min = map.containerPointToLayerPoint(size.multiplyBy(-p)).round();
		const sz = size.multiplyBy(1 + p * 2).round();
		const dpr = window.devicePixelRatio || 1;

		this._topLeft = min;
		this._center = map.getCenter();
		this._zoom = map.getZoom();

		if (this._w !== sz.x || this._h !== sz.y || this._dpr !== dpr) {
			this._w = sz.x;
			this._h = sz.y;
			this._dpr = dpr;
			canvas.width = Math.round(sz.x * dpr);
			canvas.height = Math.round(sz.y * dpr);
			canvas.style.width = `${sz.x}px`;
			canvas.style.height = `${sz.y}px`;

			this._cols = Math.ceil(sz.x / DEDUPE_CELL) + 2;
			this._rows = Math.ceil(sz.y / DEDUPE_CELL) + 2;
			this._seen = new Uint8Array(this._cols * this._rows);
		}

		this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		L.DomUtil.setPosition(canvas, min);

		this._draw();
	},

	// ---------- drawing ----------
	_draw() {
		const map = this._map;
		const ctx = this._ctx;
		if (!map || !ctx || !this._w) return;

		const w = this._w;
		const h = this._h;
		ctx.clearRect(0, 0, w, h);

		const drawn = { x: [], y: [], s: [], i: [] };
		this._drawn = drawn;
		this._grid = null;

		const scale = map.options.crs.scale(map.getZoom());
		const origin = map.getPixelOrigin();
		const offX = origin.x + this._topLeft.x;
		const offY = origin.y + this._topLeft.y;
		const cols = this._cols;
		const seen = this._seen;

		this._groups.forEach((group) => {
			if (group.type === 'circle') {
				const r = group.r;
				const st = group.style;
				seen.fill(0);
				ctx.beginPath();

				for (let si = 0; si < group.series.length; si += 1) {
					const s = group.series[si];
					const { mx, my, n } = s;
					for (let i = 0; i < n; i += 1) {
						const x = mx[i] * scale - offX;
						if (!(x >= -r && x <= w + r)) continue; // also skips NaN
						const y = my[i] * scale - offY;
						if (!(y >= -r && y <= h + r)) continue;

						if (DEDUPE_ENABLED) {
							const cell = ((y / DEDUPE_CELL) | 0) * cols + ((x / DEDUPE_CELL) | 0);
							if (seen[cell]) continue;
							seen[cell] = 1;
						}

						ctx.moveTo(x + r, y);
						ctx.arc(x, y, r, 0, TWO_PI);

						if (s.hittable) {
							drawn.x.push(x);
							drawn.y.push(y);
							drawn.s.push(s._idx);
							drawn.i.push(i);
						}
					}
				}

				ctx.globalAlpha = st.fillOpacity !== undefined && st.fillOpacity !== null ? st.fillOpacity : 0.2;
				ctx.fillStyle = st.fillColor || st.color || '#3388ff';
				ctx.fill();

				if (st.weight !== 0) {
					ctx.globalAlpha = st.opacity !== undefined && st.opacity !== null ? st.opacity : 1;
					ctx.lineWidth = st.weight !== undefined && st.weight !== null ? st.weight : 3;
					ctx.strokeStyle = st.color || '#3388ff';
					ctx.stroke();
				}
				ctx.globalAlpha = 1;
				return;
			}

			if (group.type === 'diamond') {
				ctx.fillStyle = '#f75a00';
				ctx.strokeStyle = '#000000';
				ctx.lineWidth = 1;
				for (let si = 0; si < group.series.length; si += 1) {
					const s = group.series[si];
					const { mx, my, n } = s;
					for (let i = 0; i < n; i += 1) {
						const x = mx[i] * scale - offX;
						if (!(x >= -10 && x <= w + 10)) continue;
						const y = my[i] * scale - offY;
						if (!(y >= -10 && y <= h + 10)) continue;

						ctx.save();
						ctx.translate(x + 1, y + 1);
						ctx.rotate(DIAMOND_ANGLE);
						ctx.fillRect(-7, -7, 14, 14);
						ctx.strokeRect(-7.5, -7.5, 15, 15);
						ctx.restore();

						if (s.hittable) {
							drawn.x.push(x);
							drawn.y.push(y);
							drawn.s.push(s._idx);
							drawn.i.push(i);
						}
					}
				}
				return;
			}

			// image sprite
			const sprite = loadSprite(group.url, () => this._schedule());
			if (!sprite.loaded) return;
			const [iw, ih] = group.size;
			const [ax, ay] = group.anchor;

			for (let si = 0; si < group.series.length; si += 1) {
				const s = group.series[si];
				const { mx, my, n } = s;
				for (let i = 0; i < n; i += 1) {
					const x = mx[i] * scale - offX;
					if (!(x >= -iw && x <= w + iw)) continue;
					const y = my[i] * scale - offY;
					if (!(y >= -ih && y <= h + ih)) continue;

					ctx.drawImage(sprite.img, x - ax, y - ay, iw, ih);

					if (s.hittable) {
						drawn.x.push(x);
						drawn.y.push(y);
						drawn.s.push(s._idx);
						drawn.i.push(i);
					}
				}
			}
		});
	},

	// ---------- hit testing (lazy pixel grid) ----------
	_buildGrid() {
		const d = this._drawn;
		const grid = new Map();
		for (let k = 0; k < d.x.length; k += 1) {
			const key = (Math.floor(d.y[k] / HIT_CELL) + 64) * 4096 + (Math.floor(d.x[k] / HIT_CELL) + 64);
			const bucket = grid.get(key);
			if (bucket) bucket.push(k);
			else grid.set(key, [k]);
		}
		this._grid = grid;
	},

	_hitAt(layerPoint) {
		if (!this._drawn || !this._topLeft || !layerPoint) return -1;
		if (!this._grid) this._buildGrid();

		const px = layerPoint.x - this._topLeft.x;
		const py = layerPoint.y - this._topLeft.y;
		const cx = Math.floor(px / HIT_CELL);
		const cy = Math.floor(py / HIT_CELL);
		const R = this._hitCells;
		const d = this._drawn;
		let best = -1;

		for (let dy = -R; dy <= R; dy += 1) {
			for (let dx = -R; dx <= R; dx += 1) {
				const bucket = this._grid.get((cy + dy + 64) * 4096 + (cx + dx + 64));
				if (!bucket) continue;

				for (let b = 0; b < bucket.length; b += 1) {
					const k = bucket[b];
					if (k <= best) continue; // later = drawn on top

					const group = this._series[d.s[k]].group;
					const ox = px - d.x[k];
					const oy = py - d.y[k];
					const box = group.box;
					if (ox < box[0] || ox > box[2] || oy < box[1] || oy > box[3]) continue;
					if (group.type === 'circle' && ox * ox + oy * oy > (group.r + 2) * (group.r + 2)) continue;
					best = k;
				}
			}
		}
		return best;
	},

	// ---------- hover tooltip / click popup ----------
	_closeTip() {
		if (this._tip) {
			this._tip.remove();
			this._tip = null;
		}
		if (this._hoverKey !== null && this._map) {
			L.DomUtil.removeClass(this._map.getContainer(), 'leaflet-interactive');
		}
		this._hoverKey = null;
	},

	_onMouseMove(e) {
		this._lastMove = e;
		if (this._hoverRaf) return;
		this._hoverRaf = requestAnimationFrame(() => {
			this._hoverRaf = null;
			this._handleHover(this._lastMove);
		});
	},

	_handleHover(e) {
		const map = this._map;
		if (!map || !e || !this._drawn) return;
		if (map._animatingZoom) return;
		if (map.dragging && map.dragging.moving && map.dragging.moving()) return;

		const k = this._hitAt(e.layerPoint);
		if (k < 0) {
			if (this._hoverKey !== null) this._closeTip();
			return;
		}

		const d = this._drawn;
		const key = `${d.s[k]}:${d.i[k]}`;
		if (key === this._hoverKey) return;

		this._closeTip();
		this._hoverKey = key;

		const series = this._series[d.s[k]];
		const index = d.i[k];
		L.DomUtil.addClass(map.getContainer(), 'leaflet-interactive');

		if (series.tooltipAt) {
			const html = lazyHtml(() => series.tooltipAt(index))();
			if (html) {
				this._tip = L.tooltip({ direction: 'top', ...series.tooltipOptions })
					.setLatLng([series.lats[index], series.lngs[index]])
					.setContent(html);
				this._tip.openOn(map);
			}
		}
	},

	_onClick(e) {
		const map = this._map;
		if (!map || !this._drawn) return;

		const k = this._hitAt(e.layerPoint);
		if (k < 0) return;

		const d = this._drawn;
		const series = this._series[d.s[k]];
		if (!series.popupAt) return;

		const index = d.i[k];
		const html = lazyHtml(() => series.popupAt(index))();
		if (!html) return;

		const popupAnchor = series.group.popupAnchor || [0, 0];
		L.popup({ offset: L.point(popupAnchor), ...series.popupOptions })
			.setLatLng([series.lats[index], series.lngs[index]])
			.setContent(html)
			.openOn(map);
	},
});

// ============================================================
// PERFORMANCE: BULK LAYER
//
// `items` is a flat, memoized list of descriptors:
//   { kind: 'points'  | 'markers', lats[], lngs[], ... }  -> ONE canvas
//   { kind: 'polyline', positions, style, tooltip?, ... }  -> Leaflet
//        polylines (positions may be a multi-polyline, i.e. an array
//        of arrays of [lat, lng], so many segments = ONE layer).
// Polylines are few (one per trajectory), are drawn on the map's
// canvas renderer, are added in time-sliced chunks and get their
// tooltips lazily.
// ============================================================
const BULK_FRAME_BUDGET_MS = 8;
const BULK_MIN_PER_FRAME = 20;

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

const BulkLayer = ({ items }) => {
	const map = useMap();

	useEffect(() => {
		if (!items || items.length === 0) return undefined;

		const seriesItems = [];
		const lineItems = [];
		items.forEach((item) => {
			if (item.kind === 'polyline') lineItems.push(item);
			else seriesItems.push(item);
		});

		let pointsLayer = null;
		if (seriesItems.length > 0) {
			pointsLayer = new CanvasPointsLayer(seriesItems);
			pointsLayer.addTo(map);
		}

		let group = null;
		let cancelled = false;
		let rafId = null;
		let handleMouseOver = null;
		let handleClick = null;

		if (lineItems.length > 0) {
			group = L.featureGroup().addTo(map);
			let cursor = 0;

			handleMouseOver = (e) => {
				const layer = e.layer;
				const item = layer && layer._bulkItem;
				if (!item || !item.tooltip || layer._bulkTooltipBound) return;
				layer._bulkTooltipBound = true;
				layer.bindTooltip(lazyHtml(item.tooltip), item.tooltipOptions || {});
				layer.openTooltip(e.latlng);
			};

			handleClick = (e) => {
				const layer = e.layer;
				const item = layer && layer._bulkItem;
				if (!item || !item.popup || layer._bulkPopupBound) return;
				layer._bulkPopupBound = true;
				layer.bindPopup(lazyHtml(item.popup), item.popupOptions || {});
				layer.openPopup(e.latlng);
			};

			group.on('mouseover', handleMouseOver);
			group.on('click', handleClick);

			const step = () => {
				if (cancelled) return;

				const frameStart = now();
				let builtThisFrame = 0;

				while (cursor < lineItems.length) {
					const item = lineItems[cursor];
					cursor += 1;
					builtThisFrame += 1;

					const layer = L.polyline(item.positions, item.style);
					if (item.tooltip || item.popup) layer._bulkItem = item;
					group.addLayer(layer);

					if (
						builtThisFrame >= BULK_MIN_PER_FRAME &&
						now() - frameStart > BULK_FRAME_BUDGET_MS
					) {
						break;
					}
				}

				if (cursor < lineItems.length) {
					rafId = requestAnimationFrame(step);
				}
			};

			rafId = requestAnimationFrame(step);
		}

		return () => {
			cancelled = true;
			if (rafId) cancelAnimationFrame(rafId);
			if (group) {
				group.off('mouseover', handleMouseOver);
				group.off('click', handleClick);
				map.removeLayer(group);
			}
			if (pointsLayer) {
				map.removeLayer(pointsLayer);
			}
		};
	}, [map, items]);

	return null;
};

BulkLayer.propTypes = {
	items: PropTypes.array,
};

BulkLayer.defaultProps = {
	items: [],
};

// ============================================================
// SEAMAP
// ============================================================

const SeaMap = ({
	center,
	zoom,
	style,
	mapContainerProps,
	layersControlChildren,
	mapChildren,
	detectionTrack,
	detectionMeta,
	detectionLabel,
	detectionInfo,
	trackColor,
	pipelineTracks,
	fiberTracks,
	pipelineBorderTracks,
	optionalPathTracks,
	kmpMarkers,
	// wbMarkers,
	// tpMarkers,
	// tlpMarkers,
	chMarkers,
	detectionTrajectories,
	showTrajectoryPlayControl,
	asnData,
	showFullscreenControl,
	showTopToolbar,
	showBottomInfoBar,
	pidsRegionData,
	pigPids,
	enableAISVessels,
	showAISVessels,
	aisVesselsUrl,
	aisPollIntervalMs,
	aisBbox,
	aisIncludeKinds,
	aisMaxVessels,
	aisFallbackBbox,
	aisstreamApiKey,

}) => {
	const hasTrack =
		Array.isArray(detectionTrack) &&
		detectionTrack.length > 0;

	const color =
		trackColor ||
		TRACK_COLORS[0];

	const trackPositions = hasTrack
		? detectionTrack.map((point) => [
			point.lat,
			point.lon
		])
		: [];

	const firstPoint = hasTrack
		? detectionTrack[0]
		: null;

	const cpaPoint = hasTrack
		? (
			detectionTrack.find(
				(point) => point.isCPA
			) ||
			detectionTrack[
				detectionTrack.length - 1
			]
		)
		: null;

	const [playAllTrajectories, setPlayAllTrajectories] = useState(false);

	// Index of the newest point revealed on the longest trajectory.
	// -1 = not playing / nothing revealed yet.
	const [playStep, setPlayStep] = useState(-1);

	const trajectoryIdsKey = detectionTrajectories.map((trajectory) => trajectory.id).join(',');

	const playOverlayLabel = (langdata && langdata.Play) ? langdata.Play : 'Play';

	const handlePlayOverlayChange = useCallback((isOn) => {
		setPlayAllTrajectories(isOn);
	}, []);

	// Always animate the CURRENT trajectories, even if the parent rebuilds
	// the array (new reference, same data) while Play is running.
	const detectionTrajectoriesRef = useRef(detectionTrajectories);
	useEffect(() => {
		detectionTrajectoriesRef.current = detectionTrajectories;
	}, [detectionTrajectories]);

	const playTimerRef = useRef(null);

	useEffect(() => {
		const stopTimer = () => {
			if (playTimerRef.current) {
				clearInterval(playTimerRef.current);
				playTimerRef.current = null;
			}
		};

		stopTimer();

		if (!playAllTrajectories) {
			setPlayStep(-1);
			return undefined;
		}

		const isValid = (point) =>
			point && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng));

		// Point count of the longest trajectory.
		const getMaxLen = () =>
			(detectionTrajectoriesRef.current || []).reduce(
				(max, trajectory) =>
					Math.max(max, (trajectory.points || []).filter(isValid).length),
				0
			);

		const initialMax = getMaxLen();
		if (initialMax === 0) {
			setPlayStep(-1);
			return undefined;
		}

		// Keep the whole animation to roughly 20s max, 80ms min per step,
		// 500ms max per step.
		const delay = Math.min(500, Math.max(80, Math.floor(20000 / initialMax)));

		let step = 0;
		setPlayStep(0); // show the starting point immediately

		playTimerRef.current = setInterval(() => {
			step += 1;
			const lastIndex = getMaxLen() - 1;

			if (step >= lastIndex) {
				setPlayStep(Math.max(lastIndex, 0));
				stopTimer(); // every trajectory has now revealed all its points
				return;
			}
			setPlayStep(step);
		}, delay);

		return stopTimer;
	}, [playAllTrajectories, trajectoryIdsKey]);

	const [showDetectionPoints, setShowDetectionPoints] = useState(false);
	const [showFibreDetection, setShowFibreDetection] = useState(true);
	const [showDetectionPolyline, setShowDetectionPolyline] = useState(false);
	const [showShipFibreLink, setShowShipFibreLink] = useState(false);

	// ---- Real-time (realSource*) toggles — OFF by default ----
	const [showRealtimePoints, setShowRealtimePoints] = useState(false);
	const [showRealtimeLines, setShowRealtimeLines] = useState(false);

	// Only offer the overlays if at least one detection actually has real-time points.
	const hasRealtimeData = useMemo(
		() => (detectionTrajectories || []).some((t) => t.realPoints && t.realPoints.length > 0),
		[detectionTrajectories]
	);

	// ---- ASN vessel toggles ----
	const [showAsnPoints, setShowAsnPoints] = useState(false);
	const [showAsnLines, setShowAsnLines] = useState(false);

	// ASNdata item = { name, latitude[], longitude[], speed[], datetime[] } (index-aligned)
	// -> { id, name, points: [{ lat, lng, index, speed, datetime }] }
	const toArray = (v) => {
		if (Array.isArray(v)) return v;
		if (v === null || v === undefined || v === '') return [];
		return [v];
	};

	// PERFORMANCE: memoized on asnData so the AIS trajectories (and therefore
	// the AIS bulk items) are only rebuilt when the AIS data itself changes —
	// not on every Play-animation step / state change.
	const asnTrajectories = useMemo(() => (asnData || [])
		.map((item, itemIndex) => {
			const lats = toArray(item?.latitude);
			const lngs = toArray(item?.longitude);
			const speeds = toArray(item?.speed);
			const times = toArray(item?.datetime);

			const points = [];
			lats.forEach((lat, index) => {
				const lng = lngs[index];
				if (
					lat == null || lng == null ||
					!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))
				) {
					return;
				}
				points.push({
					lat: Number(lat),
					lng: Number(lng),
					index,
					speed: speeds[index] ?? null,
					datetime: times[index] ?? null,
				});
			});

			return {
				id: `asn-${itemIndex}-${item?.name ?? ''}`,
				name: item?.name ? item.name : 'No Data',
				points,
			};
		})
		.filter((asn) => asn.points.length > 0), [asnData]);

	// ---- "Group" overlay — see groupedAlgorithmTrajectories below for
	// how the lines it draws are built. Off by default, exactly like
	// the other additive overlays (Lines, Ship-Fibre Link).
	// const [showGroupedAlgorithm, setShowGroupedAlgorithm] = useState(false);

	const wrapperRef = useRef(null);
	const positionSpanRef = useRef(null);

	const [mapInstance, setMapInstance] = useState(null);

	const handleMapReady = useCallback((map) => {
		setMapInstance(map);
	}, []);

	const [layerVisibility, setLayerVisibility] = useState({
		seaMarks: true,
		harbours: true,
		aerialPhoto: false,
		coordinateGrid: true,
		marineTraffic: true,
		compassRose: false,
		waterDepth: false,
		depthContours: true,
		aisVessels: !!showAISVessels,
	});

	const toggleLayer = useCallback((key) => {
		setLayerVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
	}, []);

	const depthContoursParams = useMemo(() => ({
		layers: 'openseamap:contour2,openseamap:contour',
		version: '1.1.0',
		format: 'image/png',
		transparent: true,
	}), []);

	const waterDepthParams = useMemo(() => ({
		layers: 'gebco2021:gebco_2021',
		version: '1.1.1',
		format: 'image/png',
		transparent: true,
	}), []);

	const allPidsData = useMemo(() => {
		if (typeof window === 'undefined' || !dashboardtype) return [];
		try {
			const raw = localStorage.getItem(`${dashboardtype.toUpperCase()}_PIDS`);
			const parsed = raw ? JSON.parse(raw) : [];
			return Array.isArray(parsed) ? parsed : [];
		} catch (err) {
			return [];
		}
	}, []);

	const pidsMarkers = useMemo(() => {
		let source = [];

		if (
			(allPidsData.length > 0 && pidsRegionData && pidsRegionData.length === 0) ||
			pidsRegionData === undefined
		) {
			source = allPidsData;
		} else if (pidsRegionData && pidsRegionData.length > 0) {
			source = pidsRegionData;
		}

		return source
			.map((item) => ({
				lat: parseFloat(item.latitude),
				lng: parseFloat(item.longitude),
				value: item.value,
				label: item.label,
			}))
			.filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
	}, [allPidsData, pidsRegionData]);

	// ============================================================
	// GROUP BY ALGORITHM — for the "Group" overlay.
	//
	// Pools every trajectory that shares the same algorithm NAME into a
	// single group, then draws ONE polyline per group.
	//
	// ORDERING RULE: trajectories are sorted by their own earliest
	// timestamp (the minimum parsed value across that trajectory's
	// startTimeHover), ascending. Each trajectory's OWN points stay
	// in their original internal order (that's already the correct
	// chronological path for that one detection) — we only reorder which
	// trajectory comes first/second/third, then concatenate their point
	// lists in that trajectory order. This avoids zig-zagging the line
	// back and forth if two trajectories' time ranges overlap, since we
	// never interleave individual points from different trajectories.
	//
	// Grouping key: the algorithm's display name (trimmed). Falls back to
	// the algorithm type ID when a trajectory has no name.
	//
	// Groups with fewer than 2 total points are filtered out.
	// ============================================================
	// const groupedAlgorithmTrajectories = useMemo(() => {
	// 	const groups = {};

	// 	(detectionTrajectories || []).forEach((traj) => {
	// 		const trimmedName =
	// 			traj.algorithmName && String(traj.algorithmName).trim()
	// 				? String(traj.algorithmName).trim()
	// 				: null;

	// 		const vesselName =
	// traj.vesselName &&
	// String(traj.vesselName).trim() ? String(traj.vesselName).trim() : 'Unknown Vessel';

	// 		const key = `vessel:${vesselName}`;

	// 		if (!groups[key]) {
	// 			groups[key] = {
	// 				key,
	// 				algorithmName:
	//     trimmedName ||
	//     ((langdata && langdata.Unknown) ? langdata.Unknown: 'Unknown'),

	// 				vesselName,

	// 				color:
	//     getVesselColorKey(vesselName) ||
	//     traj.color ||
	//     null,

	// 				trajectories: [],
	// 			};
	// 		}

	// 		const points = traj.points || [];

	// 		// Build this trajectory's own point list, tagging each point
	// 		// with its own timestamp, but WITHOUT reordering them
	// 		// relative to each other — a trajectory's own points already
	// 		// form a coherent chronological path.
	// 		const trajPoints = [];
	// 		let minTimeMs = null;

	// 		points.forEach((point, pointIndex) => {
	// 			if (point.lat == null || point.lng == null) return;
	// 			if (!Number.isFinite(Number(point.lat)) || !Number.isFinite(Number(point.lng))) return;

	// 			const rawTime = pickValueForIndex(traj.startTimeHover, pointIndex);
	// 			const parsedTime = rawTime ? dayjs(rawTime) : null;
	// 			const timeMs = parsedTime && parsedTime.isValid() ? parsedTime.valueOf() : null;

	// 			if (timeMs !== null && (minTimeMs === null || timeMs < minTimeMs)) {
	// 				minTimeMs = timeMs;
	// 			}

	// 			trajPoints.push({
	// 				lat: Number(point.lat),
	// 				lng: Number(point.lng),
	// 				time: rawTime || null,
	// 				timeMs,
	// 				trajectoryId: traj.id,
	// 				channelName: traj.channelName,
	// 				pointIndex,
	// 			});
	// 		});

	// 		if (trajPoints.length === 0) return;

	// 		groups[key].trajectories.push({
	// 			trajectoryId: traj.id,
	// 			// Trajectories with no resolvable timestamp are pushed to
	// 			// the end (Infinity) rather than breaking the sort or
	// 			// being dropped.
	// 			minTimeMs: minTimeMs === null ? Infinity : minTimeMs,
	// 			points: trajPoints,
	// 		});
	// 	});

	// 	return Object.values(groups)
	// 		.map((group) => {
	// 			// The ONE rule that decides connection order: sort the
	// 			// TRAJECTORIES (not individual points) by each
	// 			// trajectory's own earliest timestamp, ascending. Stable
	// 			// sort keeps same-timestamp trajectories in their
	// 			// original relative order.
	// 			const sortedTrajectories = [...group.trajectories].sort(
	// 				(a, b) => a.minTimeMs - b.minTimeMs
	// 			);

	// 			// Concatenate each trajectory's own points, in their own
	// 			// original order, one trajectory after another.
	// 			const points = sortedTrajectories.flatMap((t) => t.points);

	// 			return { ...group, points };
	// 		})
	// 		.filter((group) => group.points.length > 1);
	// }, [detectionTrajectories]);

	const isValidLatLng = (lat, lng) =>
		typeof lat === 'number' && !isNaN(lat) &&
		typeof lng === 'number' && !isNaN(lng);

	const safeCenter = isValidLatLng(center?.lat, center?.lng)
		? [center.lat, center.lng]
		: Array.isArray(center) && isValidLatLng(center[0], center[1])
			? center
			: [0, 0];


	const vesselColorMap = useMemo(() => {
		const map = {};
		let colorIndex = 0;

		(detectionTrajectories || []).forEach((trajectory) => {
			const vesselName = getVesselColorKey(
				trajectory.vesselName
			);

			if (!(vesselName in map)) {
				map[vesselName] =
					ALGORITHM_COLORS[colorIndex % ALGORITHM_COLORS.length
					];

				colorIndex += 1;
			}
		});

		return map;
	}, [detectionTrajectories]);

	// ============================================================
	// DETECTION ITEMS — everything passed as ARRAYS
	//
	//   • source points    -> ONE points-series per trajectory
	//   • start circles    -> ONE points-series per trajectory
	//   • start markers    -> ONE markers-series for ALL trajectories
	//   • fibre detection  -> ONE diamond-series for ALL trajectories
	//   • lines            -> ONE polyline per trajectory
	//   • ship-fibre links -> ONE multi-polyline per trajectory
	// Hover / popup content is produced lazily by index.
	// ============================================================
	const detectionItems = useMemo(() => {
		const items = [];

		const startMarkers = { lats: [], lngs: [], refs: [] };
		const fibreMarkers = { lats: [], lngs: [], dists: [] };

		(detectionTrajectories || []).forEach((trajectory, trajIndex) => {

			const vesselName = getVesselColorKey(
				trajectory.vesselName
			);

			const trajColor =
				vesselColorMap[vesselName] ||
				trajectory.color ||
				ALGORITHM_COLORS[trajIndex % ALGORITHM_COLORS.length
				];

			const points =
				trajectory.points || [];

			const trajFirstPoint =
				points[0];

			// One fixed set of hover fields per trajectory — see
			// DetectionHoverContent above. Every point along this
			// trajectory (including the starting marker) shows the
			// same trajectory-level info, since PIDS/Channel/
			// Algorithm/fibre-length/start-time are all properties
			// of the detection as a whole, not of an individual
			// point.
			const trajInfo = {
				detectionAlgorithmTypeName:
					trajectory.algorithmName,
				channelName:
					trajectory.channelName,
				pidsName:
					trajectory.pidsName,
				startFiberLength:
					trajectory.startFiberLength,
				endFiberLength:
					trajectory.endFiberLength,
				startTimeHover:
					trajectory.startTimeHover,
				vesselName:
					trajectory.vesselName,
				sourceSpeedms:
					trajectory.sourceSpeedms,
				sourceDistances:
					trajectory.sourceDistances,
				fiberDistance:
					trajectory.fiberDistance,
				seeddatetime:
					trajectory.seeddatetime,
				trackType:
					trajectory.trackType,
				seedStartDistance:
					trajectory.seedStartDistance,
				seedEndDistance:
					trajectory.seedEndDistance,
				sourceTimeStart:
					trajectory.sourceTimeStart,
				sourceTimeEnd:
					trajectory.sourceTimeEnd,
			};

			const pointStyle = {
				color: '#ffffff',
				weight: 1,
				fillColor: trajColor,
				fillOpacity: 0.9
			};

			// ---- LINES ----
			if (points.length > 1 && showDetectionPolyline) {
				items.push({
					kind: 'polyline',
					positions: points.map((point) => [point.lat, point.lng]),
					style: {
						color: trajColor,
						weight: 3,
						opacity: 0.85
					},
				});
			}

			// ---- POINTS (index 0 is drawn as the start circle + marker) ----
			if (showDetectionPoints && points.length > 1) {
				const rest = points.slice(1);
				items.push({
					kind: 'points',
					lats: rest.map((p) => p.lat),
					lngs: rest.map((p) => p.lng),
					radius: 5,
					style: pointStyle,
					tooltipOptions: { direction: 'top', offset: [0, -4] },
					tooltipAt: (i) => (
						<DetectionHoverContent
							info={trajInfo}
							pointIndex={i + 1}
							totalPoints={points.length}
						/>
					),
				});
			}

			// ---- START POINT: circle + (shared) start marker ----
			if (trajFirstPoint) {
				items.push({
					kind: 'points',
					lats: [trajFirstPoint.lat],
					lngs: [trajFirstPoint.lng],
					radius: 5,
					style: pointStyle,
				});

				startMarkers.lats.push(trajFirstPoint.lat);
				startMarkers.lngs.push(trajFirstPoint.lng);
				startMarkers.refs.push({
					trajectory,
					info: trajInfo,
					totalPoints: points.length,
				});
			}

			// ---- FIBRE DETECTION MARKER(S) ----
			// Built from the parallel fiberLatitude / fiberLongitude /
			// fiberDistance arrays (one fibre point per index — mirrors
			// how sourceLatitude/sourceLongitude line up for the ship
			// track points above). Filters out any index that doesn't
			// resolve to a valid lat/lng pair.
			const fiberPoints = buildFiberPoints(trajectory);

			if (showFibreDetection) {
				fiberPoints.forEach((fiberPoint) => {
					fibreMarkers.lats.push(fiberPoint.lat);
					fibreMarkers.lngs.push(fiberPoint.lng);
					fibreMarkers.dists.push(fiberPoint.distance);
				});
			}

			// ---- SHIP-FIBRE LINK ----
			// Connects each ship-track point to its own fibre point
			// at the same index (point[i] <-> fiberPoints[i]).
			// All segments of one trajectory = ONE multi-polyline.
			if (showShipFibreLink) {
				const segments = [];

				points.forEach((point, pointIndex) => {
					const fiberPoint = fiberPoints[pointIndex];

					if (!fiberPoint || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
						return;
					}

					segments.push([
						[point.lat, point.lng],
						[fiberPoint.lat, fiberPoint.lng],
					]);
				});

				if (segments.length > 0) {
					items.push({
						kind: 'polyline',
						positions: segments,
						style: {
							color: trajColor,
							weight: 3.5,
							dashArray: '4,5',
							opacity: 1,
						},
					});
				}
			}
		});

		// ---- shared marker series (one array for ALL trajectories) ----
		if (startMarkers.lats.length > 0) {
			items.push({
				kind: 'markers',
				icon: vesselCpaIcon,
				lats: startMarkers.lats,
				lngs: startMarkers.lngs,
				tooltipOptions: { direction: 'top', offset: [0, -40] },
				tooltipAt: (i) => (
					<DetectionHoverContent
						info={startMarkers.refs[i].info}
						pointIndex={0}
						totalPoints={startMarkers.refs[i].totalPoints}
					/>
				),
				popupAt: (i) => (
					<>
						<strong>
							{(langdata && langdata.StartingPoint)
								? langdata.StartingPoint
								: 'Starting Point'}
						</strong>
						<br />
						{startMarkers.refs[i].trajectory.channelName ||
							startMarkers.refs[i].trajectory.label ||
							(langdata && langdata.RealtimeDetection
								? langdata.RealtimeDetection
								: 'Detection')}
					</>
				),
			});
		}

		if (fibreMarkers.lats.length > 0) {
			items.push({
				kind: 'markers',
				shape: 'diamond',
				lats: fibreMarkers.lats,
				lngs: fibreMarkers.lngs,
				tooltipOptions: { direction: 'top', offset: [0, -40] },
				tooltipAt: (i) => (
					<>
						<strong>{(langdata && langdata.FibreDetection ? langdata.FibreDetection : 'Fibre Detection')}</strong>
						<br />
						{(langdata && langdata.FibreDistance ? langdata.FibreDistance + ' (m)' : 'Fibre Distance (m)')}
						{': '}
						{fibreMarkers.dists[i] !== null && fibreMarkers.dists[i] !== undefined ? fibreMarkers.dists[i] : 'No Data'}
					</>
				),
			});
		}

		return items;
	}, [
		detectionTrajectories,
		vesselColorMap,
		showDetectionPolyline,
		showDetectionPoints,
		showFibreDetection,
		showShipFibreLink,
	]);

	// ============================================================
	// REAL-TIME ITEMS (realSource* arrays) — off until toggled on.
	//
	// Plotted exactly like the algorithm trajectories (same vessel colour,
	// same canvas points, same lazy hover + DetectionHoverContent). The
	// only differences: points have a dark outline and lines are dashed,
	// so they can be told apart; hover distance / speed / start-time come
	// from the realSource* / realTimeStart arrays.
	// ============================================================
	const realtimeItems = useMemo(() => {
		const items = [];
		if (!showRealtimePoints && !showRealtimeLines) return items;

		(detectionTrajectories || []).forEach((trajectory, trajIndex) => {
			const realPoints = trajectory.realPoints || [];
			if (realPoints.length === 0) return;

			const vesselName = getVesselColorKey(trajectory.vesselName);
			const trajColor =
				vesselColorMap[vesselName] ||
				trajectory.color ||
				ALGORITHM_COLORS[trajIndex % ALGORITHM_COLORS.length];

			// Same hover fields as the algorithm points; only the per-point
			// arrays are swapped for their real-time counterparts.
			const realInfo = {
				detectionAlgorithmTypeName: trajectory.algorithmName,
				channelName: trajectory.channelName,
				pidsName: trajectory.pidsName,
				startFiberLength: trajectory.startFiberLength,
				endFiberLength: trajectory.endFiberLength,
				startTimeHover: trajectory.realStartTimeHover,
				vesselName: trajectory.vesselName,
				sourceSpeedms: trajectory.realSourceSpeedms,
				sourceDistances: trajectory.realSourceDistances,
				fiberDistance: trajectory.fiberDistance,
			};

			if (showRealtimeLines && realPoints.length > 1) {
				items.push({
					kind: 'polyline',
					positions: realPoints.map((p) => [p.lat, p.lng]),
					style: {
						color: trajColor,
						weight: 3,
						opacity: 0.85,
						dashArray: '6,6',
					},
				});
			}

			if (showRealtimePoints) {
				items.push({
					kind: 'points',
					lats: realPoints.map((p) => p.lat),
					lngs: realPoints.map((p) => p.lng),
					radius: 5,
					style: {
						color: '#000000',
						weight: 1.5,
						fillColor: trajColor,
						fillOpacity: 0.9,
					},
					tooltipOptions: { direction: 'top', offset: [0, -4] },
					tooltipAt: (i) => (
						<DetectionHoverContent
							info={realInfo}
							pointIndex={realPoints[i].index}
							totalPoints={trajectory.realTotalPoints}
						/>
					),
				});
			}
		});

		return items;
	}, [detectionTrajectories, vesselColorMap, showRealtimePoints, showRealtimeLines]);

	// ============================================================
	// ASN ITEMS — one points-series (+ one line) per vessel,
	// one shared markers-series for every vessel's first point.
	// ============================================================
	const asnItems = useMemo(() => {
		const items = [];
		const firstMarkers = { lats: [], lngs: [], refs: [] };

		asnTrajectories.forEach((asn, asnIndex) => {
			const asnColor =
				colorForAlgorithmId(asn.name) ||
				ALGORITHM_COLORS[asnIndex % ALGORITHM_COLORS.length];

			const points = asn.points || [];
			const asnPointStyle = {
				color: '#ffffff',
				weight: 1,
				fillColor: asnColor,
				fillOpacity: 0.9
			};

			if (points.length > 1 && showAsnLines) {
				items.push({
					kind: 'polyline',
					positions: points.map((p) => [p.lat, p.lng]),
					style: { color: asnColor, weight: 3, opacity: 0.85 },
				});
			}

			if (showAsnPoints) {
				if (points.length > 1) {
					const rest = points.slice(1);
					items.push({
						kind: 'points',
						lats: rest.map((p) => p.lat),
						lngs: rest.map((p) => p.lng),
						radius: 5,
						style: asnPointStyle,
						tooltipOptions: { direction: 'top', offset: [0, -4] },
						tooltipAt: (i) => (
							<AsnHoverContent name={asn.name} point={rest[i]} />
						),
					});
				}

				const first = points[0];
				if (first) {
					items.push({
						kind: 'points',
						lats: [first.lat],
						lngs: [first.lng],
						radius: 5,
						style: asnPointStyle,
					});

					firstMarkers.lats.push(first.lat);
					firstMarkers.lngs.push(first.lng);
					firstMarkers.refs.push({ name: asn.name, point: first });
				}
			}
		});

		if (firstMarkers.lats.length > 0) {
			items.push({
				kind: 'markers',
				icon: aisDataVesselIcon,
				lats: firstMarkers.lats,
				lngs: firstMarkers.lngs,
				tooltipOptions: { direction: 'top', offset: [0, -40] },
				tooltipAt: (i) => (
					<AsnHoverContent name={firstMarkers.refs[i].name} point={firstMarkers.refs[i].point} />
				),
				popupAt: (i) => (
					<>
						<strong>
							{(langdata && langdata.StartingPoint) ? langdata.StartingPoint : 'Starting Point'}
						</strong>
						<br />
						{firstMarkers.refs[i].name}
					</>
				),
			});
		}

		return items;
	}, [asnTrajectories, showAsnPoints, showAsnLines]);

	// What Play should draw at the current step. Each trajectory reveals one
	// more of its own points per step, in its original order, from the
	// starting point onward. A shorter trajectory holds at its last point.
	const animatedTrajectories = useMemo(() => {
		if (!playAllTrajectories || playStep < 0) return [];

		return (detectionTrajectories || [])
			.map((trajectory, trajIndex) => {
				const valid = (trajectory.points || [])
					.map((point, originalIndex) => ({
						lat: Number(point && point.lat),
						lng: Number(point && point.lng),
						index: originalIndex,
					}))
					.filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

				if (valid.length === 0) return null;

				const revealed = valid.slice(0, Math.min(playStep, valid.length - 1) + 1);
				const head = revealed[revealed.length - 1];

				const vesselKey = getVesselColorKey(trajectory.vesselName);
				const color =
					vesselColorMap[vesselKey] ||
					trajectory.color ||
					ALGORITHM_COLORS[trajIndex % ALGORITHM_COLORS.length];

				return {
					id: trajectory.id,
					color,
					revealed,
					totalPoints: (trajectory.points || []).length,
					positions: revealed.map((point) => [point.lat, point.lng]),
					head,
					info: {
						detectionAlgorithmTypeName: trajectory.algorithmName,
						channelName: trajectory.channelName,
						pidsName: trajectory.pidsName,
						startFiberLength: trajectory.startFiberLength,
						endFiberLength: trajectory.endFiberLength,
						startTimeHover: trajectory.startTimeHover,
						vesselName: trajectory.vesselName,
						sourceSpeedms: trajectory.sourceSpeedms,
						sourceDistances: trajectory.sourceDistances,
						fiberDistance: trajectory.fiberDistance,
					},
				};
			})
			.filter(Boolean);
	}, [playAllTrajectories, playStep, detectionTrajectories, vesselColorMap]);

	// Play: every revealed point of every trajectory = ONE points-series per
	// trajectory on the shared canvas (non-interactive, like before).
	const playItems = useMemo(() => animatedTrajectories
		.filter((anim) => anim.revealed.length > 0)
		.map((anim) => ({
			kind: 'points',
			lats: anim.revealed.map((p) => p.lat),
			lngs: anim.revealed.map((p) => p.lng),
			radius: 4,
			style: {
				color: '#ffffff',
				weight: 1,
				fillColor: anim.color,
				fillOpacity: 1,
			},
		})), [animatedTrajectories]);


	return (
		<div
			ref={wrapperRef}
			style={{
				position: 'relative',
				height: '100%',
				width: '100%'
			}}
		>
			{showTopToolbar && (
				<TopToolbar
					map={mapInstance}
					layerVisibility={layerVisibility}
					onToggleLayer={toggleLayer}
					showAisMenuItem={enableAISVessels}
				/>
			)}

			<MapContainer
				center={safeCenter}
				zoom={zoom}
				maxZoom={22}
				scrollWheelZoom={true}
				// PERFORMANCE: draw vector layers (Polylines) on a single canvas
				// instead of one SVG node per path. A caller can still override
				// this through mapContainerProps.
				preferCanvas={true}
				style={
					style || {
						height: '100%',
						width: '100%'
					}
				}
				{...mapContainerProps}
			>

				<MapReadyBridge onReady={handleMapReady} />
				<MapAutoCenter detectionTrajectories={detectionTrajectories} pidsMarkers={pidsMarkers} />
				{showTrajectoryPlayControl && detectionTrajectories.length > 0 && (
					<PlayOverlayListener name={playOverlayLabel} onChange={handlePlayOverlayChange} />
				)}
				{showBottomInfoBar && <MapPositionBridge targetRef={positionSpanRef} />}

				{showFullscreenControl && (
					<FullscreenControl position="topleft" fullscreenTargetRef={wrapperRef} />
				)}

				{layerVisibility.aerialPhoto ? (
					<TileLayer
						url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
						attribution='Tiles &copy; <a href="https://www.esri.com">Esri</a> — Source: Esri, Maxar, Earthstar Geographics'
						maxZoom={22}
						maxNativeZoom={19}
					/>
				) : (
					<TileLayer
						attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
						url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
						maxZoom={22}
						maxNativeZoom={19}
					/>
				)}

				{layerVisibility.seaMarks && (
					<TileLayer
						attribution='&copy; <a href="http://openseamap.org">OpenSeaMap</a> contributors'
						url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
						maxZoom={22}
						maxNativeZoom={18}
					/>
				)}

				{layerVisibility.waterDepth && (
					<WMSTileLayer
						url="https://geoserver.openseamap.org/geoserver/gwc/service/wms"
						params={waterDepthParams}
						opacity={0.8}
						attribution="GEBCO 2021 Bathymetry"
					/>
				)}

				{layerVisibility.depthContours && (
					<WMSTileLayer
						url="https://depth.openseamap.org/geoserver/openseamap/wms"
						params={depthContoursParams}
						attribution='&copy; <a href="https://www.openseamap.org">OpenSeaMap</a> depth contours'
					/>
				)}

				{layerVisibility.marineTraffic && (
					<TileLayer
						url="https://tiles.marinetraffic.com/ais_helpers/shiptilesingle.aspx?output=png&sat=1&grouping=shiptype&tile_size=512&legends=1&zoom={z}&X={x}&Y={y}"
						tileSize={512}
						attribution='&copy; <a href="https://www.marinetraffic.com">MarineTraffic</a>'
					/>
				)}

				{layerVisibility.coordinateGrid && <CoordinateGrid />}

				{layerVisibility.harbours && <HarbourLayer />}

				{layerVisibility.compassRose && <CompassRoseControl />}

				{enableAISVessels && layerVisibility.aisVessels && (
					<AisVesselsLayer
						vesselsUrl={aisVesselsUrl}
						pollIntervalMs={aisPollIntervalMs}
						fixedBbox={aisBbox}
						includeKinds={aisIncludeKinds}
						maxVessels={aisMaxVessels}
						fallbackBbox={aisFallbackBbox}
						aisstreamApiKey={aisstreamApiKey}
					/>
				)}

				{pidsMarkers.map((item, index) => (
					<React.Fragment key={`pids-${index}`}>
						<Marker position={[item.lat, item.lng]} icon={pidsIcon}>
							{item.label && (
								<Tooltip direction="top" offset={[0, -28]}>
									{item.label}
								</Tooltip>
							)}
							<Popup>{item.label}</Popup>
						</Marker>

						{pigPids &&
							pigPids.map(
								(pigValue, pigIndex) =>
									pigValue === item.value && (
										<Circle
											key={`pids-flag-${index}-${pigIndex}`}
											center={[item.lat, item.lng]}
											radius={300}
											pathOptions={{ color: 'red', fillOpacity: 0 }}
										/>
									)
							)}
					</React.Fragment>
				))}

				<LayersControl position="topright">

					<Overlay
						checked
						name={oneviewtypeLabeluper}
					>
						<>
							{pipelineTracks &&
								pipelineTracks.map(
									(track, index) => (
										<Polyline
											key={`pipeline-${index}`}
											pathOptions={
												pipecolor
											}
											weight={4}
											positions={track}
										/>
									)
								)}
						</>
					</Overlay>

					<Overlay name="Fiber">
						<>
							{fiberTracks &&
								fiberTracks.map(
									(track, index) => (
										<Polyline
											key={`fiber-${index}`}
											pathOptions={
												odmetercolor
											}
											weight={4}
											positions={track}
										/>
									)
								)}
						</>
					</Overlay>

					<Overlay name="Optional Path">
						<>
							{optionalPathTracks &&
								optionalPathTracks.map(
									(track, index) => (
										<Polyline
											key={`optional-path-${index}`}
											pathOptions={
												optionalcolor
											}
											weight={4}
											positions={track}
										/>
									)
								)}
						</>
					</Overlay>

					<Overlay name={(langdata && langdata.KMP) ? langdata.KMP : 'KMP'}>
						<MarkerClusterGroup>
							{kmpMarkers && kmpMarkers.map((marker, index) => (
								<Marker key={`kmp-${index}`} position={marker.geocode} icon={KMPicon}>
									<Popup>
										{marker.pop}
										<div>{(langdata && langdata.Landmark) ? langdata.Landmark : 'Landmark'}: {marker.landmarkDescription}</div>
										<div>{(langdata && langdata.Chainage) ? langdata.Chainage + ' (m)' : 'Chainage (m)'}: {marker.pipelineChainage}</div>
										<div>{(langdata && langdata.FiberLength) ? langdata.FiberLength : 'Fiber Length (m)'}: {marker.ODMeter}</div>
									</Popup>
								</Marker>
							))}
						</MarkerClusterGroup>
					</Overlay>

					{/* <Overlay name={(langdata && langdata.WB) ? langdata.WB : 'WB'}>
						<MarkerClusterGroup>
							{wbMarkers && wbMarkers.map((marker, index) => (
								<Marker key={`wb-${index}`} position={marker.geocode} icon={WBicon}>
									<Popup>
										{marker.pop}
										<div>{(langdata && langdata.Landmark) ? langdata.Landmark : 'Landmark'}: {marker.landmarkDescription}</div>
										<div>{(langdata && langdata.Chainage) ? langdata.Chainage : 'Chainage'}: {marker.pipelineChainage}</div>
									</Popup>
								</Marker>
							))}
						</MarkerClusterGroup>
					</Overlay> */}

					{/* <Overlay name={(langdata && langdata.TP) ? langdata.TP : 'TP'}>
						<MarkerClusterGroup>
							{tpMarkers && tpMarkers.map((marker, index) => (
								<Marker key={`tp-${index}`} position={marker.geocode} icon={TPicon}>
									<Popup>
										{marker.pop}
										<div>{(langdata && langdata.Landmark) ? langdata.Landmark : 'Landmark'}: {marker.landmarkDescription}</div>
										<div>{(langdata && langdata.Chainage) ? langdata.Chainage : 'Chainage'}: {marker.pipelineChainage}</div>
									</Popup>
								</Marker>
							))}
						</MarkerClusterGroup>
					</Overlay> */}

					{/* <Overlay name={(langdata && langdata.TLP) ? langdata.TLP : 'TLP'}>
						<MarkerClusterGroup>
							{tlpMarkers && tlpMarkers.map((marker, index) => (
								<Marker key={`tlp-${index}`} position={marker.geocode} icon={TLPicon}>
									<Popup>
										{marker.pop}
										<div>{(langdata && langdata.Landmark) ? langdata.Landmark : 'Landmark'}: {marker.landmarkDescription}</div>
										<div>{(langdata && langdata.Chainage) ? langdata.Chainage : 'Chainage'}: {marker.pipelineChainage}</div>
									</Popup>
								</Marker>
							))}
						</MarkerClusterGroup>
					</Overlay> */}

					<Overlay name={(langdata && langdata.CH) ? langdata.CH : 'CH'}>
						<MarkerClusterGroup>
							{chMarkers && chMarkers.map((marker, index) => (
								<Marker key={`ch-${index}`} position={marker.geocode} icon={CHicon}>
									<Popup>
										{marker.pop}
										<div>{(langdata && langdata.Landmark) ? langdata.Landmark : 'Landmark'}: {marker.landmarkDescription}</div>
										<div>{(langdata && langdata.Chainage) ? langdata.Chainage : 'Chainage'}: {marker.pipelineChainage}</div>
									</Popup>
								</Marker>
							))}
						</MarkerClusterGroup>
					</Overlay>

					<Overlay name="System Border">
						<>
							{pipelineBorderTracks &&
								pipelineBorderTracks.map(
									(track, index) => (
										<Polyline
											key={`pipeline-border-${index}`}
											pathOptions={
												bordercolor
											}
											opacity={0.35}
											weight={25}
											positions={track}
										/>
									)
								)}
						</>
					</Overlay>

					{showTrajectoryPlayControl && detectionTrajectories.length > 0 && (
						<Overlay name={playOverlayLabel}>
							{/* This layer's own add/remove events are no longer what
							    drives Play — PlayOverlayListener (mounted directly
							    inside MapContainer) listens to the map's
							    overlayadd/overlayremove events instead, which
							    Leaflet's LayersControl fires reliably even for
							    overlay entries that were registered after the
							    control first mounted (exactly this case, since
							    this entry only exists once detections have
							    loaded). Kept as a harmless fallback in case it
							    does fire. */}
							<Polyline
								positions={[]}
								eventHandlers={{
									add: () => setPlayAllTrajectories(true),
									remove: () => setPlayAllTrajectories(false)
								}}
							/>
						</Overlay>
					)}

					{detectionTrajectories.length > 0 && (
						<>
							<Overlay
								// checked
								name={(langdata && langdata.Points) ? langdata.Points : 'Points'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowDetectionPoints(true),
										remove: () => setShowDetectionPoints(false)
									}}
								/>
							</Overlay>
							<Overlay
								checked
								name={(langdata && langdata.FibreDetection) ? langdata.FibreDetection : 'Fibre Detection'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowFibreDetection(true),
										remove: () => setShowFibreDetection(false)
									}}
								/>
							</Overlay>

							<Overlay
								name={(langdata && langdata.Lines) ? langdata.Lines : 'Lines'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowDetectionPolyline(true),
										remove: () => setShowDetectionPolyline(false)
									}}
								/>
							</Overlay>
							<Overlay
								name={(langdata && langdata.ShipFibreLink) ? langdata.ShipFibreLink : 'Ship-Fibre Link'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowShipFibreLink(true),
										remove: () => setShowShipFibreLink(false)
									}}
								/>
							</Overlay>

							{/* ---- REAL-TIME overlays (unchecked by default) ---- */}
							{hasRealtimeData && (
								<>
									<Overlay
										name={(langdata && langdata.RealtimePoints) ? langdata.RealtimePoints : 'Realtime Points'}
									>
										<Polyline
											positions={[]}
											eventHandlers={{
												add: () => setShowRealtimePoints(true),
												remove: () => setShowRealtimePoints(false)
											}}
										/>
									</Overlay>

									<Overlay
										name={(langdata && langdata.RealtimeLines) ? langdata.RealtimeLines : 'Realtime Lines'}
									>
										<Polyline
											positions={[]}
											eventHandlers={{
												add: () => setShowRealtimeLines(true),
												remove: () => setShowRealtimeLines(false)
											}}
										/>
									</Overlay>
								</>
							)}


							{/* ---- GROUP ----
							    Draws one polyline per algorithm name, connecting
							    every trajectory that shares that algorithm in
							    ascending "trajectory start time" order — each
							    trajectory's own points stay in their original
							    order (see groupedAlgorithmTrajectories above).
							    Off by default, just like Lines / Ship-Fibre Link. */}
							{/* <Overlay
								name={(langdata && langdata.Group) ? langdata.Group : 'Group'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowGroupedAlgorithm(true),
										remove: () => setShowGroupedAlgorithm(false)
									}}
								/>
							</Overlay> */}

						</>
					)}
					{asnTrajectories.length > 0 && (
						<>
							<Overlay
								// checked
								name={(langdata && langdata.AISPoints) ? langdata.AISPoints : 'AIS Points'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowAsnPoints(true),
										remove: () => setShowAsnPoints(false)
									}}
								/>
							</Overlay>

							<Overlay
								name={(langdata && langdata.AISLines) ? langdata.AISLines : 'AIS Lines'}
							>
								<Polyline
									positions={[]}
									eventHandlers={{
										add: () => setShowAsnLines(true),
										remove: () => setShowAsnLines(false)
									}}
								/>
							</Overlay>
						</>
					)}

					{layersControlChildren}

				</LayersControl>

				{mapChildren}

				{hasTrack && (
					<>

						<Polyline
							positions={trackPositions}
							pathOptions={{
								color,
								weight: 3,
								opacity: 0.8
							}}
						/>

						{detectionTrack.map(
							(point, index) => {

								if (point.isCPA) {
									return null;
								}

								return (
									<CircleMarker
										key={`detection-pt-${index}`}
										center={[
											point.lat,
											point.lon
										]}
										radius={5}
										pathOptions={{
											color: '#ffffff',
											weight: 1,
											fillColor: color,
											fillOpacity: 0.9
										}}
									>
										<Tooltip
											direction="top"
											offset={[0, -4]}
										>
											<DetectionHoverContent
												info={detectionInfo}
											// pointIndex={index}
											/>
										</Tooltip>

										<Popup>
											<strong>
												{detectionLabel ||
													'Detection'}
											</strong>

											<br />

											Point #{index + 1}
											{' '}
											of
											{' '}
											{detectionTrack.length}

											<br />

											Lat:
											{' '}
											{point.lat.toFixed(6)}

											<br />

											Lon:
											{' '}
											{point.lon.toFixed(6)}

											{point.sbr !== null &&
												point.sbr !== undefined && (
												<>
													<br />
														SBR:
													{' '}
													{point.sbr}
												</>
											)}

											{point.sourceAngle !== null &&
												point.sourceAngle !== undefined && (
												<>
													<br />
														Source angle:
													{' '}
													{point.sourceAngle}°
												</>
											)}

											{point.maxBeamEnergy !== null &&
												point.maxBeamEnergy !== undefined && (
												<>
													<br />
														Max beam energy:
													{' '}
													{point.maxBeamEnergy}
												</>
											)}

											{point.sourceDistance !== null &&
												point.sourceDistance !== undefined && (
												<>
													<br />
														Source distance:
													{' '}
													{point.sourceDistance} m
												</>
											)}

											{point.timeStart && (
												<>
													<br />
													Start:
													{' '}
													{point.timeStart}
												</>
											)}

											{point.timeEnd && (
												<>
													<br />
													End:
													{' '}
													{point.timeEnd}
												</>
											)}
										</Popup>
									</CircleMarker>
								);
							}
						)}

						{firstPoint && (
							<Marker
								position={[
									firstPoint.lat,
									firstPoint.lon
								]}
								icon={labelIcon(
									'',
									'#16a34a'
								)}
							>
								<Tooltip
									direction="top"
									offset={[0, -10]}
								>
									<DetectionHoverContent
										info={detectionInfo}
									/>
								</Tooltip>

								<Popup>
									Start of detection track
								</Popup>
							</Marker>
						)}

						{cpaPoint && (
							<Marker
								position={[
									cpaPoint && cpaPoint.lat,
									cpaPoint && cpaPoint.lon
								]}
								icon={labelIcon(
									'',
									''
								)}
							>
								<Tooltip
									direction="top"
									offset={[0, -40]}
								>
									<DetectionHoverContent
										info={detectionInfo}
									/>
								</Tooltip>

								<Popup>
									<strong>
										{detectionLabel ||
											'Detection'}
									</strong>

									{cpaPoint && cpaPoint.sourceAngle !== null &&
										cpaPoint.sourceAngle !== undefined && (
										<>
											<br />
												Angle:
											{' '}
											{cpaPoint.sourceAngle}°
										</>
									)}

									{cpaPoint && cpaPoint.sbr !== null &&
										cpaPoint.sbr !== undefined && (
										<>
											<br />
												SBR:
											{' '}
											{cpaPoint.sbr}
										</>
									)}

									{detectionMeta &&
										detectionMeta.sourceSpeedKts !== undefined &&
										detectionMeta.sourceSpeedKts !== null && (
										<>
											<br />
												Speed:
											{' '}
											{detectionMeta.sourceSpeedKts}
											{' '}
												kn
										</>
									)}

									{detectionMeta &&
										detectionMeta.sourceSpeedms !== undefined &&
										detectionMeta.sourceSpeedms !== null && (
										<>
											<br />
												Speed:
											{' '}
											{detectionMeta.sourceSpeedms}
											{' '}
												m/s
										</>
									)}

									{detectionMeta &&
										detectionMeta.cpaSourceDistance !== undefined &&
										detectionMeta.cpaSourceDistance !== null && (
										<>
											<br />
												CPA distance:
											{' '}
											{detectionMeta.cpaSourceDistance}
											{' '}
												m
										</>
									)}

									{detectionMeta &&
										detectionMeta.cpaSourceDatetime && (
										<>
											<br />
												CPA time:
											{' '}
											{detectionMeta.cpaSourceDatetime}
										</>
									)}
								</Popup>
							</Marker>
						)}
					</>
				)}

				{/* PERFORMANCE: detection trajectories (points / lines / start
				    markers / fibre-detection diamonds / ship-fibre links) are
				    passed as ARRAYS and drawn on ONE canvas — see
				    CanvasPointsLayer / BulkLayer. */}
				<BulkLayer items={detectionItems} />

				{/* Real-time (realSource*) points / lines — off until toggled on. */}
				<BulkLayer items={realtimeItems} />

				{/* AIS (ASN) trajectories. */}
				<BulkLayer items={asnItems} />

				{/* ============================================================
				    GROUPED-ALGORITHM POLYLINES — rendered only while the
				    "Group" overlay checkbox is checked. One Polyline per
				    algorithm name, walking through groupedAlgorithmTrajectories'
				    pre-sorted (by trajectory start time, ascending) point list.
				    Colored the same way individual trajectories from that
				    algorithm are colored, so it reads as "the same algorithm,
				    now joined up across channels/trajectories in time order."
				   ============================================================ */}
				{/* {groupedAlgorithmTrajectories.map((group) => {
					const positions = group.points.map((point) => [point.lat, point.lng]);
					const groupColor = group.color || '#000000';

					return (
						<Polyline
							key={`grouped-algorithm-${group.key}`}
							positions={positions}
							pathOptions={{
								color: groupColor,
								weight: 5,
								opacity: 1,
								lineCap: 'round',
								lineJoin: 'round',
							}}
						>
							<Tooltip sticky>
								{(langdata && langdata.Algorithm ? langdata.Algorithm : 'Algorithm')}
								{': '}
								{group.algorithmName}
								<br />
								{(langdata && langdata.Points ? langdata.Points : 'Points')}
								{': '}
								{group.points.length}
							</Tooltip>
						</Polyline>
					);
				})} */}

				{/* PLAY: revealed points on the shared canvas ... */}
				{playAllTrajectories && <BulkLayer items={playItems} />}

				{/* ... plus the path drawn so far and the moving head per trajectory. */}
				{playAllTrajectories && animatedTrajectories.map((anim) => (
					<React.Fragment key={`play-trajectory-${anim.id}`}>
						{/* Path drawn so far */}
						{anim.positions.length > 1 && (
							<Polyline
								positions={anim.positions}
								pathOptions={{ color: '#085820', weight: 4, opacity: 0.95 }}
							/>
						)}

						{/* Moving head of the trajectory */}
						<Marker position={[anim.head.lat, anim.head.lng]} icon={vesselCpaIcon}>
							<Tooltip direction="top" offset={[0, -40]}>
								<DetectionHoverContent info={anim.info} pointIndex={anim.head.index} totalPoints={anim.totalPoints} />
							</Tooltip>
						</Marker>
					</React.Fragment>
				))}

				<ScaleControl position="bottomleft" />

			</MapContainer>

			{showBottomInfoBar && <BottomInfoBar map={mapInstance} positionRef={positionSpanRef} />}
		</div>
	);
};

// ============================================================
// PROP TYPES
// ============================================================

SeaMap.propTypes = {
	center: PropTypes.oneOfType([
		PropTypes.arrayOf(
			PropTypes.number
		),
		PropTypes.object
	]),

	zoom: PropTypes.number,

	style: PropTypes.object,

	mapContainerProps:
		PropTypes.object,

	layersControlChildren:
		PropTypes.node,

	mapChildren:
		PropTypes.node,

	detectionTrack:
		PropTypes.arrayOf(
			PropTypes.shape({
				lat: PropTypes.number,
				lon: PropTypes.number,
				sbr: PropTypes.number,
				sourceAngle:
					PropTypes.number,
				maxBeamEnergy:
					PropTypes.number,
				sourceDistance:
					PropTypes.number,
				timeStart:
					PropTypes.string,
				timeEnd:
					PropTypes.string,
				isCPA:
					PropTypes.bool
			})
		),

	detectionMeta:
		PropTypes.shape({
			sourceSpeedms:
				PropTypes.number,
			sourceSpeedKts:
				PropTypes.number,
			cpaSourceDatetime:
				PropTypes.string,
			cpaSourceDistance:
				PropTypes.number,
			eventPositions:
				PropTypes.array
		}),

	detectionLabel:
		PropTypes.string,

	detectionInfo:
		PropTypes.shape({
			detectionAlgorithmTypeName:
				PropTypes.string,
			channelName:
				PropTypes.string,
			pidsName:
				PropTypes.string,
			startFiberLength:
				PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			endFiberLength:
				PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			startTimeHover:
				PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		}),

	trackColor:
		PropTypes.string,

	pipelineTracks:
		PropTypes.arrayOf(
			PropTypes.arrayOf(
				PropTypes.array
			)
		),

	fiberTracks:
		PropTypes.arrayOf(
			PropTypes.arrayOf(
				PropTypes.array
			)
		),

	pipelineBorderTracks:
		PropTypes.arrayOf(
			PropTypes.arrayOf(
				PropTypes.array
			)
		),

	optionalPathTracks:
		PropTypes.arrayOf(
			PropTypes.arrayOf(
				PropTypes.array
			)
		),

	// ROU marker layers (KMP / WB / TP / TLP / CH) — same shape as built
	// by RealtimeEvents.jsx from `mapdata`:
	// { geocode: [lat, lng], pop: string, landmarkDescription, pipelineChainage }
	kmpMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	wbMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	tpMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	tlpMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	chMarkers:
		PropTypes.arrayOf(
			PropTypes.shape({
				geocode: PropTypes.arrayOf(PropTypes.number),
				pop: PropTypes.string,
				landmarkDescription: PropTypes.string,
				pipelineChainage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			})
		),

	// One entry per detection cluster item from ShipTracker's
	// DetectionClusterData. `points` are built from the parallel
	// sourceLatitude / sourceLongitude arrays, in order (index 0 gets
	// the vessel-CPA icon, the rest render as plain circle markers).
	// `fiberLatitude` / `fiberLongitude` / `fiberDistance` are the
	// parallel arrays used to plot the Fibre Detection marker(s) and
	// the Ship-Fibre Link lines. `realPoints` and the realSource* arrays
	// feed the Realtime Points / Realtime Lines overlays.
	detectionTrajectories:
		PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([
					PropTypes.string,
					PropTypes.number
				]),

				color:
					PropTypes.string,

				label:
					PropTypes.string,

				eventType:
					PropTypes.string,

				channelName:
					PropTypes.string,

				algorithmName:
					PropTypes.string,

				detectionAlgorithmTypeId:
					PropTypes.oneOfType([
						PropTypes.string,
						PropTypes.number
					]),

				pidsName:
					PropTypes.string,

				startFiberLength:
					PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

				endFiberLength:
					PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

				// Already display-ready (localized) — rendered as-is on hover.
				startTimeHover:
					PropTypes.oneOfType([
						PropTypes.string,
						PropTypes.number
					]),

				fiberLatitude:
					PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),

				fiberLongitude:
					PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),

				fiberDistance:
					PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),

				points:
					PropTypes.arrayOf(
						PropTypes.shape({
							lat:
								PropTypes.number,

							lng:
								PropTypes.number,
						})
					),

				realPoints:
					PropTypes.arrayOf(
						PropTypes.shape({
							lat: PropTypes.number,
							lng: PropTypes.number,
							index: PropTypes.number,
						})
					),

				realTotalPoints:
					PropTypes.number,

				realSourceDistances:
					PropTypes.array,

				realSourceSpeedms:
					PropTypes.array,

				realStartTimeHover:
					PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
			})
		),
	asnData: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
			longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
			speed: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
			datetime: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
		})
	),

	showTrajectoryPlayControl:
		PropTypes.bool,

	showFullscreenControl:
		PropTypes.bool,

	showTopToolbar:
		PropTypes.bool,

	showBottomInfoBar:
		PropTypes.bool,

	pidsRegionData:
		PropTypes.array,

	pigPids:
		PropTypes.array,

	hiddenTrajectoryCpaIds:
		PropTypes.array,

	enableAISVessels:
		PropTypes.bool,

	showAISVessels:
		PropTypes.bool,

	aisVesselsUrl:
		PropTypes.string,

	aisPollIntervalMs:
		PropTypes.number,

	aisBbox:
		PropTypes.arrayOf(PropTypes.number),

	aisIncludeKinds:
		PropTypes.arrayOf(PropTypes.string),

	aisMaxVessels:
		PropTypes.number,

	// Broad [south, west, north, east] box queried when the local
	// viewport has no vessels. MUST stay under openwaters' 100 sq°
	// anonymous cap — it's clamped automatically if not, but keep it
	// small and deliberate (see AIS_FALLBACK_BBOX). Pass null/undefined
	// to disable fallback behavior entirely. NOTE: fallback vessels are
	// rendered at their real position; the map is never panned to them.
	aisFallbackBbox:
		PropTypes.arrayOf(PropTypes.number),

	// Optional aisstream.io API key used as a last-resort snapshot
	// fetch if openwaters' fallback box also comes back empty. Safe
	// to omit — that tier is simply skipped without one.
	aisstreamApiKey:
		PropTypes.string,
};

// ============================================================
// DEFAULT PROPS
// ============================================================

SeaMap.defaultProps = {
	center: undefined,

	zoom: 11,

	style: undefined,

	mapContainerProps: {},

	layersControlChildren:
		null,

	mapChildren:
		null,

	detectionTrack: [],

	detectionMeta:
		null,

	detectionLabel:
		'',

	detectionInfo:
		null,

	trackColor:
		undefined,

	pipelineTracks:
		[],

	fiberTracks:
		[],

	pipelineBorderTracks:
		[],

	optionalPathTracks:
		[],

	kmpMarkers:
		[],

	wbMarkers:
		[],

	tpMarkers:
		[],

	tlpMarkers:
		[],

	chMarkers:
		[],

	detectionTrajectories:
		[],
	asnData: [],

	showTrajectoryPlayControl:
		true,

	showFullscreenControl:
		true,

	showTopToolbar:
		true,

	showBottomInfoBar:
		true,

	pidsRegionData:
		undefined,

	pigPids:
		[],

	hiddenTrajectoryCpaIds:
		[],

	enableAISVessels:
		true,

	showAISVessels:
		false,

	aisVesselsUrl:
		OPENWATERS_VESSELS_URL,

	aisPollIntervalMs:
		15000,

	aisBbox:
		undefined,

	aisIncludeKinds:
		['vessel'],

	aisMaxVessels:
		500,

	aisFallbackBbox:
		AIS_FALLBACK_BBOX,

	aisstreamApiKey:
		undefined,
};

export default SeaMap;